import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AiChatBotEx - CrashSight AI 一站式对话助手组件
 *
 * 对应 TAPD 需求：
 * - 【CS】AI在线分析一站式入口 (--story=131432604)
 * - 【CS】CrashClaw 全链路自动化工作流集成
 *
 * 该组件取代原有的 AiChatBot 组件，主要变更：
 * 1. 聊天接口从 /api/aiChat/v1/chat-messages 更换为 /api/knotAgent/chat，请求时携带当前项目 appId
 * 2. SSE 通信协议从自定义格式更换为 AG-UI 协议（见 ag-ui-protocol.ts）
 * 3. 会话历史接口 /api/knotAgent/chatHistory 仅返回会话元信息（不含消息）
 * 4. 会话消息通过独立接口 /api/knotAgent/chatMessages 按 conversationId 获取（与旧版 AiChatBot 对齐）
 *
 * === 组件结构 ===
 *
 * === 工作区与对话引擎（统一抽屉）===
 *
 * 底部工作区选择器决定走哪条后端链路（二者互斥，UI 同时挂载、display 切换）：
 *   - workspaceSelection === 'ai_agent'（CrashSight AI）→ Knot Agent（/api/knotAgent/*，改造前链路）
 *   - 其它值为 CrashClaw workspaceUid           → CrashClaw（/redir/api/crashclaw/* + Bridge WS）
 *
 * 顶层通过 activeProduct（由 unifiedWorkspaceSelection 推导）区分渲染哪一块内容区：
 *
 * 1. AI Agent（CrashSight AI）—— 智能崩溃分析助手
 *    - 首页：欢迎 Banner + 四大场景卡片（问题分析/大盘统计/数据查询/问答）
 *    - 场景子页面：点击卡片进入，含场景图标/标题/副标题 + 输入框
 *    - 对话模式：气泡列表 + 底部输入框，支持 SSE 流式响应
 *    - 全屏模式：左侧历史会话列表 + 右侧对话区域
 *
 * 2. CrashClaw —— 全链路自动化工作流引擎
 *    - 未配置状态：引导卡片（3步配置说明 + 前往配置按钮）
 *    - 已配置状态：四大场景卡片（全链路分析/自动分单/代码修复/工作流监控）+ 输入框
 *    - 全屏模式下会话列表渲染为空（保持布局一致）
 *
 * 两个产品 Tab 共享 header 和输入框组件（AiChatBotInputBox），
 * 对话记录（chatHistoryList）仅归属 AI Agent，CrashClaw 始终显示自身首页。
 *
 * === 待实现（后续迭代）===
 * - CrashClaw 配置状态的接口判定（当前 isCrashClawConfigured 硬编码为 false）
 * - CrashClaw 配置页面跳转逻辑（handleGoToCrashClawConfig）
 * - CrashClaw 场景卡片点击交互
 * - CrashClaw "你还可以直接问我" 推荐问题区域
 * - CrashClaw 独立的对话会话管理
 * - 推荐问题区域和"换一换"功能
 * - 输入框附加功能按钮（添加附件、编辑、历史记录、语音）
 * - 分析报告卡片生成和展示
 * - 回复底部追问建议按钮
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button, Divider, Alert, Popover, Tooltip, message, Input, Modal, } from 'antd';
import useZuStore from 'store/zustand/zustand-store';
import { ze } from 'utils/zhEn';
import { EnvUtil } from 'utils/env-util';
import { CloseOutlined, ExpandOutlined, CompressOutlined, PlusOutlined, LeftOutlined, RightOutlined, BulbOutlined, ClockCircleOutlined, EditOutlined, SettingOutlined, DownOutlined, CheckOutlined, } from '@ant-design/icons';
// @ts-ignore - SVG
import ScenarioIssueAnalysisIcon from 'svg/v2/newcs_aiassistant_scenario_issue_analysis.svg';
// @ts-ignore - SVG
import ScenarioDashboardStatsIcon from 'svg/v2/newcs_aiassistant_scenario_dashboard_stats.svg';
// @ts-ignore - SVG
import ScenarioDataQueryIcon from 'svg/v2/newcs_aiassistant_scenario_data_query.svg';
// @ts-ignore - SVG
import ScenarioQaIcon from 'svg/v2/newcs_aiassistant_scenario_qa.svg';
// @ts-ignore - SVG (CrashClaw 源码分析卡专用副本，id 前缀 sa- 以免与代码修复卡撞车)
import ClawSourceIcon from 'svg/v2/newcs_aiassistant_crashclaw_source.svg';
// @ts-ignore - SCSS module
import scss from './AiDrawer.scss';
import { useLocalStorageStateOfAiChatBotEx } from 'utils/react-hooks/cs-local-storage-hooks';
// @ts-ignore - JS component
import AiChatBotInputBox from 'components/top/AiDrawer/AiChatBotInputBox';
// @ts-ignore - JS component
import AiDrawerAutoScrollBody from 'components/top/AiDrawer/AiDrawerAutoScrollBody';
// @ts-ignore - JS component
import AiChatBotBubble from 'components/top/AiDrawer/AiChatBotBubble';
// @ts-ignore - JS component
import AiChatBotConversationList, { filterConversationList, } from 'components/top/AiDrawer/AiChatBotConversationList';
// @ts-ignore - JS component
import SearchBox from 'components/commons/SearchBox';
import { useGetState } from 'ahooks';
// @ts-ignore - JS module
import RestHelper from 'utils/RestHelper';
// @ts-ignore - JS module
import { AiAnalysisType } from 'utils/constants/issue-ai-status';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import WelcomeLogoIcon from 'svg/v2/newcs_aiassistant_welcome_logo.svg';
import { AgUiEventType, parseAgUiEvent, extractConversationId, extractMessageId, } from './ag-ui-protocol';
import ToolCallPanel from './ToolCallPanel';
import ThinkingPanel from './ThinkingPanel';
import CrashClawChatPanel from './CrashClawChatPanel';
import AiIssueAnalysisDrawerBody from 'components/top/AiDrawer/AiIssueAnalysisDrawerBody';
import UnifiedWorkspacePicker from './UnifiedWorkspacePicker';
import { buildCrashClawConfigUrl } from 'components/user/CrashClawConfig/crashClawWorkspaceUtil';
import { DEFAULT_LLM_MODEL, VENUS_LLM_MODELS } from 'components/user/CrashClawConfig/crashClawDefaults';
import { findPendingToolCallIndex, markAllToolCallsCompleted, markToolCallCompleted, upsertToolCallItem, } from './crashClawChatUtil';
// ============================================================
// 场景意图枚举 & 卡片配置
// ============================================================
/**
 * AI 对话场景意图枚举
 * 用户进入某个场景后，聊天接口会携带该 intention 参数，后端根据此参数调整分析策略
 */
export var AiIntention;
(function (AiIntention) {
    AiIntention["ISSUE_ANALYSIS"] = "ISSUE_ANALYSIS";
    AiIntention["DASHBOARD_STATS"] = "DASHBOARD_STATS";
    AiIntention["DATA_QUERY"] = "DATA_QUERY";
    AiIntention["SMART_QA"] = "SMART_QA";
})(AiIntention || (AiIntention = {}));
const SCENARIO_CARDS = [
    {
        intention: AiIntention.ISSUE_ANALYSIS,
        IconSvg: ScenarioIssueAnalysisIcon,
        title: ze('问题分析', 'Issue Analysis'),
        subtitle: ze('智能分析崩溃根因', 'AI-powered crash root cause analysis'),
        pageTitle: ze('智能分析崩溃根因，快速定位问题', 'AI Root Cause Analysis for Crashes'),
        pageSubtitle: ze('请描述你需要分析的崩溃问题', 'Describe the crash issue you want to analyze'),
    },
    {
        intention: AiIntention.DASHBOARD_STATS,
        IconSvg: ScenarioDashboardStatsIcon,
        title: ze('大盘统计', 'Dashboard Stats'),
        subtitle: ze('智能生成数据报表', 'AI-generated data reports'),
        pageTitle: ze('智能生成数据报表，洞察全局趋势', 'AI-Generated Reports & Trend Insights'),
        pageSubtitle: ze('请描述你需要统计的数据维度', 'Describe the data dimensions you want to analyze'),
    },
    {
        intention: AiIntention.DATA_QUERY,
        IconSvg: ScenarioDataQueryIcon,
        title: ze('数据查询', 'Data Query'),
        subtitle: ze('智能查询崩溃数据', 'Smart crash data querying'),
        pageTitle: ze('智能查询崩溃数据，精准获取结果', 'Smart Crash Data Queries & Results'),
        pageSubtitle: ze('请描述你需要查询的崩溃数据', 'Describe the crash data you want to query'),
    },
    {
        intention: AiIntention.SMART_QA,
        IconSvg: ScenarioQaIcon,
        title: ze('问答', 'Smart Q&A'),
        subtitle: ze('智能回答CrashSight功能指引', 'AI answers for CrashSight guidance'),
        pageTitle: ze('智能问答，快速了解CrashSight功能', 'Smart Q&A for CrashSight Features'),
        pageSubtitle: ze('请描述你想了解的CrashSight功能', 'Describe the CrashSight feature you want to learn about'),
    },
];
/**
 * 各场景的快捷推荐问题，null key 表示主场景（未进入任何子场景时展示）
 */
const QUICK_SUGGEST_MAP = {
    HOME: [
        ze('请帮我查询昨天Top10崩溃问题', 'List yesterday\'s Top 10 crash issues'),
        ze('查询昨天各机型的崩溃设备数Top排行', 'Show yesterday\'s crash device count ranking by model'),
        ze('对Top3崩溃问题进行根因分析', 'Perform root cause analysis on the Top 3 crashes'),
    ],
    [AiIntention.ISSUE_ANALYSIS]: [
        ze('帮我分析这个崩溃的根本原因是什么', 'Help me analyze the root cause of this crash'),
        ze('这个堆栈信息提示什么问题？', 'What does this stack trace indicate?'),
        ze('为什么会出现这种类型的崩溃？', 'Why does this type of crash occur?'),
    ],
    [AiIntention.DASHBOARD_STATS]: [
        ze('帮我生成最近一周的崩溃质量报表', 'Generate a crash quality report for the past week'),
        ze('对比本周和上周的崩溃数据，有什么变化？', 'Compare this week\'s crash data with last week'),
        ze('按影响程度排序，列出最严重的问题', 'List the most severe issues ranked by impact'),
    ],
    [AiIntention.DATA_QUERY]: [
        ze('最近有哪些新增的崩溃问题？', 'What are the newly added crash issues recently?'),
        ze('找出影响用户最多的崩溃', 'Find the crashes that affect the most users'),
        ze('查找未处理的高优先级问题', 'Find unresolved high-priority issues'),
    ],
    [AiIntention.SMART_QA]: [
        ze('我可以用MCP做什么？', 'What can I do with MCP?'),
        ze('MCP 电子令牌如何获取？', 'How to get an MCP token?'),
        ze('堆栈无法还原的原因如何排查？', 'How to troubleshoot stack symbolication failures?'),
    ],
};
const ISSUE_AI_APP_WORKSPACE_PREFIX = 'issue_ai_app:';
const ISSUE_AI_SESSION_ID_SEPARATOR = '::';
/** 会话列表里没有真实 conversationId 的占位值，不能作为 conversationId 传给后端 */
const ISSUE_AI_CONVERSATION_NEW = '__new__';
/** 与后端 DifyService.OFFLINE_SESSION_CONVERSATION_ID 保持一致：离线初步分析没有会话 ID */
const ISSUE_AI_CONVERSATION_OFFLINE = '__offline__';
function isPlaceholderIssueAiConversationId(conversationId) {
    return conversationId === ISSUE_AI_CONVERSATION_NEW || conversationId === ISSUE_AI_CONVERSATION_OFFLINE;
}
/** 统一工作区 localStorage 值规范化（兼容旧值 AI_AGENT / issue_ai:{issueId} / W-codebuddy-global 等） */
function normalizeWorkspaceSelection(raw) {
    if (!raw || raw === 'ai_agent' || raw === 'AI_AGENT')
        return 'ai_agent';
    // W-codebuddy-global 是 Global Bridge 自动创建的工作区，前端应等同于 'codebuddy' 处理
    if (raw === 'W-codebuddy-global')
        return 'codebuddy';
    if (raw.startsWith('issue_ai:') && !raw.startsWith(ISSUE_AI_APP_WORKSPACE_PREFIX)) {
        return 'ai_agent';
    }
    return raw;
}
function isAiAgentWorkspace(selection) {
    return normalizeWorkspaceSelection(selection) === 'ai_agent';
}
function isIssueAiWorkspace(selection) {
    return normalizeWorkspaceSelection(selection).startsWith(ISSUE_AI_APP_WORKSPACE_PREFIX);
}
function isCrashClawWorkspace(selection) {
    const normalized = normalizeWorkspaceSelection(selection);
    if (normalized === 'codebuddy')
        return false;
    return !isAiAgentWorkspace(normalized) && !isIssueAiWorkspace(normalized);
}
function makeIssueAiGameWorkspaceSelection(appId) {
    return `${ISSUE_AI_APP_WORKSPACE_PREFIX}${appId}`;
}
function parseAppIdFromIssueAiWorkspace(selection) {
    if (!isIssueAiWorkspace(selection))
        return '';
    return normalizeWorkspaceSelection(selection).slice(ISSUE_AI_APP_WORKSPACE_PREFIX.length);
}
function makeIssueAiSessionId(issueId, conversationId) {
    return `${issueId}${ISSUE_AI_SESSION_ID_SEPARATOR}${conversationId}`;
}
function parseIssueAiSessionId(sessionId) {
    const sepIndex = sessionId.indexOf(ISSUE_AI_SESSION_ID_SEPARATOR);
    if (sepIndex < 0) {
        return { issueId: '', conversationId: '' };
    }
    return {
        issueId: sessionId.slice(0, sepIndex),
        conversationId: sessionId.slice(sepIndex + ISSUE_AI_SESSION_ID_SEPARATOR.length),
    };
}
/** 从问题详情/上报页 URL 解析 issueId，例：/crash-reporting/crashes/{appId}/{issueId}/report */
function parseIssueIdFromPathname(pathname) {
    const m = pathname.match(/^\/crash-reporting\/(?:crashes|errors|blocks|janks|hangs|metrickit-crash)\/[^/]+\/([^/?#]+)/);
    return m?.[1] || '';
}
/** 当前项目始终出现在「问题分析」列表里。没有历史记录时接口会漏掉它。 */
export function ensureCurrentAppIssueWorkspace(list, appId) {
    const rows = list || [];
    if (!appId || rows.some((row) => row.appId === appId)) {
        return rows;
    }
    return [{ appId }, ...rows];
}
// ============================================================
// 数据转换函数
// ============================================================
/**
 * 将 chatHistory 接口的会话数据转换为 AiChatBotConversationList 组件所需格式
 *
 * 转换逻辑：
 * - id: 直接使用 conversationId
 * - name: 使用后端返回的 title 字段
 * - updated_at: 使用 updateTimeMillis 转为秒级时间戳
 *
 * 返回结果按 updated_at 降序排列（最近更新的会话排在前面）
 */
function transformConversationsToList(conversations) {
    return conversations
        .map((conv) => ({
        id: conv.conversationId,
        name: conv.title || ze('新会话', 'New Conversation'),
        updated_at: conv.updateTimeMillis
            ? moment(conv.updateTimeMillis).unix()
            : 0,
    }))
        .sort((a, b) => b.updated_at - a.updated_at);
}
/**
 * 将 chatMessages 接口返回的消息数组转换为 ChatHistoryItem[] 格式
 *
 * 每条 KnotAgentMessage 会被拆分为两条 ChatHistoryItem：
 * 1. 用户提问（isBotNotUser: false, text: userSentRequest）
 * 2. AI 回复（isBotNotUser: true, text: userReceivedResponse）
 *
 * 消息按 userRequestAt 时间升序排列，空文本消息会被过滤
 */
function transformMessagesToHistoryList(messages) {
    return [...messages]
        .sort((a, b) => a.userRequestAt - b.userRequestAt)
        .flatMap((msg) => [
        {
            messageId: msg.id,
            isBotNotUser: false,
            text: msg.userSentRequest,
        },
        {
            messageId: msg.id,
            isBotNotUser: true,
            text: msg.userReceivedResponse,
        },
    ])
        .filter((x) => x.text);
}
// ============================================================
// 问题详情页关联入口相关（见 spec: 20260417_AI助手问题分析页面关联_spec.md）
// ============================================================
// ============================================================
// 组件
// ============================================================
const AiChatBotEx = () => {
    const { t } = useTranslation();
    const aiDrawerSlice = useZuStore().aiDrawer;
    const chatBotFullscreen = aiDrawerSlice.chatBotFullscreen;
    // 从 Redux 获取当前打开项目的 appId，用于发起聊天请求
    const currentAppId = useSelector((state) => {
        try {
            return state.app.get('current').toJS().appId || '';
        }
        catch {
            return '';
        }
    });
    /** null：用户信息还没到，先按内部账号处理，避免内部用户被提前藏掉智能助手。 */
    const canUseKnotAssistant = useSelector((state) => {
        try {
            const current = state.user.get('current');
            if (!current)
                return null;
            return !!current.get('isInCrashClawWhitelist');
        }
        catch {
            return null;
        }
    });
    const issueIdToNameMap = useSelector((state) => {
        try {
            const list = state.issue.get('issueList')?.toJS?.() || [];
            const map = {};
            list.forEach((x) => {
                if (!x?.issueId)
                    return;
                map[String(x.issueId)] = x.exceptionMessage || x.exceptionName || '';
            });
            return map;
        }
        catch {
            return {};
        }
    });
    const accessibleAppList = useSelector((state) => {
        try {
            return state.app.get('appList')?.toJS?.() || [];
        }
        catch {
            return [];
        }
    });
    const appIdToDisplayName = useMemo(() => {
        const map = {};
        accessibleAppList.forEach((app) => {
            if (!app?.appId)
                return;
            map[app.appId] = app.appName || app.name || app.appId;
        });
        return map;
    }, [accessibleAppList]);
    const appIdToPid = useMemo(() => {
        const map = {};
        accessibleAppList.forEach((app) => {
            if (!app?.appId)
                return;
            if (app.pid != null) {
                map[app.appId] = app.pid;
            }
        });
        return map;
    }, [accessibleAppList]);
    // ============================================================
    // 状态管理
    // ============================================================
    // --- localStorage 持久化 ---
    // 使用独立的 localStorage key (CsAiChatBotEx)，与旧版 AiChatBot (CsAiChatBot) 隔离
    const [storageData, , patchStorageData] = useLocalStorageStateOfAiChatBotEx();
    const storageConversationId = storageData?.conversationId || '';
    // --- 会话 ID ---
    // 使用 useGetState 以便在 SSE 回调闭包中能通过 getter 获取最新值
    const [conversationId, setConversationId, getConversationId] = useGetState(storageConversationId);
    // conversationId → localStorage 的同步 effect 依赖 activeProduct，
    // 需放在 activeProduct 声明之后（见下方），此处不再定义，避免"先用后声明"。
    // conversationId 变更时同步到 localStorage
    useEffect(() => {
        if (conversationId) {
            patchStorageData({ conversationId });
        }
    }, [conversationId]);
    // 转换后的会话列表，供 AiChatBotConversationList 组件渲染
    const [conversationList, setConversationList] = useState([]);
    // --- 对话消息状态 ---
    // 使用 useGetState 以便在 SSE 回调闭包中通过 getter 获取最新值（tool call 处理需要读取最新 list）
    const [chatHistoryList, setChatHistoryList, getChatHistoryList] = useGetState([]);
    // randomId 机制：每次发送新消息时生成随机 ID，
    // 避免多个并发 SSE 请求的消息混写到同一个 answerInfo
    const [currentAnswerRandomId, setCurrentAnswerRandomId, getCurrentAnswerRandomId,] = useGetState('');
    // 当前 AI 正在输出的回复内容
    const [currentAnswerInfo, setCurrentAnswerInfo, getCurrentAnswerInfo] = useGetState(null);
    // 当前正在流式输出的思考内容（THINKING_TEXT_MESSAGE_* 事件）
    const [currentThinkingInfo, setCurrentThinkingInfo, getCurrentThinkingInfo] = useGetState(null);
    // 标记是否有正在进行的 AI 回复输出
    const [isAnswerOutputting, setIsAnswerOutputting, getIsAnswerOutputting] = useGetState(false);
    // 新会话标记：首次收到 conversationId 后置为 true，
    // 消息接收完毕（RUN_FINISHED）后刷新会话列表以显示新会话
    const [, setPrepareToRefreshConversationList, getPrepareToRefreshConversationList,] = useGetState(false);
    // 用于中止正在进行的 SSE 流式请求
    const [abortController, setAbortController] = useState(null);
    // --- 场景意图状态 ---
    // null 表示在首页（未进入任何场景），非 null 表示当前所处的场景
    const [activeIntention, setActiveIntention] = useState(null);
    // --- 模型选择状态（CodeBuddy 工作区专用） ---
    // CodeBuddy 工作区需要模型胶囊让用户切换 LLM，与 CrashClaw 面板保持一致
    const [selectedModel, setSelectedModel] = useState(DEFAULT_LLM_MODEL);
    const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
    const modelOptions = VENUS_LLM_MODELS;
    const selectedModelLabel = modelOptions.find((m) => m.value === selectedModel)?.label || selectedModel;
    // --- 产品 tab 状态 ---
    // 现已合并为统一工作区选择：'ai_agent' 表示 CrashSight AI 工作区，其它值为 CrashClaw workspaceUid
    const storageWorkspaceSelection = normalizeWorkspaceSelection(storageData?.workspaceSelection);
    const [unifiedWorkspaceSelection, setUnifiedWorkspaceSelection] = useState(storageWorkspaceSelection);
    const [wsPopoverOpen, setWsPopoverOpen] = useState(false);
    /**
     * 当前对话引擎：由工作区选择唯一推导，避免与 localStorage 不同步。
     * CrashSight AI → Knot Agent；CrashClaw 工作区 → CrashClaw。
     */
    const activeProduct = useMemo(() => {
        if (unifiedWorkspaceSelection === 'codebuddy')
            return 'CODEBUDDY';
        if (isAiAgentWorkspace(unifiedWorkspaceSelection))
            return 'AI_AGENT';
        if (isIssueAiWorkspace(unifiedWorkspaceSelection))
            return 'ISSUE_AI';
        return 'CRASHCLAW';
    }, [unifiedWorkspaceSelection]);
    const isCrashClawWorkspaceSelected = activeProduct === 'CRASHCLAW';
    const isCodeBuddyWorkspace = activeProduct === 'CODEBUDDY';
    // CODEBUDDY 复用 CrashSight AI（Knot Agent）的整套聊天 UI 与会话状态机，
    // 仅在网络层按工作区切换接口名（/redir/api/codeBuddy/*）与请求/响应字段（sessionId）。
    const usesKnotAgentUI = activeProduct === 'AI_AGENT' || activeProduct === 'CODEBUDDY';
    const isCrashSightAiWorkspace = activeProduct === 'AI_AGENT';
    // 切换 Tab 时关闭工作区 Popover，避免隐藏 Tab 上的 Popover 仍保持 open 状态
    useEffect(() => {
        setWsPopoverOpen(false);
    }, [activeProduct]);
    // conversationId 变更时同步到 localStorage
    // 注意：仅 AI_AGENT 的 conversationId 才持久化。CODEBUDDY 的 sessionId（cb- 前缀）
    // 与 AI_AGENT 共用同一份聊天状态，但不能写入共享 storage，否则重开抽屉时会被
    // 当成 AI_AGENT 会话去 knotAgent 拉取，导致串号。CODEBUDDY 会话通过历史侧栏恢复。
    useEffect(() => {
        if (activeProduct === 'AI_AGENT' && conversationId) {
            patchStorageData({ conversationId });
        }
    }, [conversationId, activeProduct]);
    // AI_AGENT 与 CODEBUDDY 共用同一份聊天状态（chatHistoryList / conversationId）。
    // 用 ref 记录当前共享聊天归属哪个产品：
    // - 切入的目标产品与归属一致（例如从 CRASHCLAW 切回 AI_AGENT）→ 保留会话，不重置；
    // - 目标产品与归属不同（AI_AGENT ↔ CODEBUDDY 互切）→ 重置共享会话并按新产品拉历史，避免串号；
    // - 首次挂载且为 AI_AGENT → 交给 chatBotTrigger 的 storage 恢复逻辑，不在此重置。
    const sharedChatOwnerRef = useRef(null);
    useEffect(() => {
        if (activeProduct !== 'AI_AGENT' && activeProduct !== 'CODEBUDDY')
            return;
        if (sharedChatOwnerRef.current === activeProduct)
            return;
        const isFirst = sharedChatOwnerRef.current === null;
        sharedChatOwnerRef.current = activeProduct;
        if (isFirst && activeProduct === 'AI_AGENT')
            return;
        if (getIsAnswerOutputting()) {
            stopCurrentConversation();
        }
        setConversationId('');
        setChatHistoryList([]);
        setCurrentAnswerInfo(null);
        setCurrentThinkingInfo(null);
        setActiveIntention(null);
        fetchChatHistory();
    }, [activeProduct]);
    // --- CrashClaw 工作区列表（提升到父组件，供统一选择器使用） ---
    const [crashClawWorkspaces, setCrashClawWorkspaces] = useState([]);
    const [crashClawWorkspacesLoaded, setCrashClawWorkspacesLoaded] = useState(false);
    const [codebuddyOnline, setCodebuddyOnline] = useState(false);
    const [issueAiGameWorkspaces, setIssueAiGameWorkspaces] = useState([]);
    const [issueAiGameWorkspacesLoaded, setIssueAiGameWorkspacesLoaded] = useState(false);
    const [issueAiHistorySessions, setIssueAiHistorySessions] = useState([]);
    const [selectedIssueAiSessionId, setSelectedIssueAiSessionId] = useState('');
    const [issueAiNewSessionModalOpen, setIssueAiNewSessionModalOpen] = useState(false);
    const [issueAiNewSessionIssueId, setIssueAiNewSessionIssueId] = useState('');
    const selectedIssueAiAppId = parseAppIdFromIssueAiWorkspace(unifiedWorkspaceSelection);
    const selectedIssueAiSession = parseIssueAiSessionId(selectedIssueAiSessionId);
    const crashClawWorkspacesRefreshTrigger = useZuStore((s) => s.aiDrawer.crashClawWorkspacesRefreshTrigger);
    const fetchCrashClawWorkspaces = useCallback(async () => {
        if (!EnvUtil.isChinaEnv()) {
            setCrashClawWorkspaces([]);
            setCrashClawWorkspacesLoaded(true);
            return;
        }
        try {
            const rsp = await RestHelper.mustPost('/redir/api/crashclaw/listWorkspaces');
            setCrashClawWorkspaces(rsp.json.data || []);
        }
        catch (e) {
            console.error('[AiChatBotEx] Failed to fetch CrashClaw workspaces', e);
        }
        finally {
            setCrashClawWorkspacesLoaded(true);
        }
    }, []);
    // 查询 Global Bridge (AI V2 / codebuddy) 在线状态
    const fetchCodebuddyHealth = useCallback(async () => {
        if (!EnvUtil.isChinaEnv()) {
            setCodebuddyOnline(false);
            return;
        }
        try {
            const rsp = await RestHelper.mustPost('/redir/api/codeBuddy/health');
            setCodebuddyOnline(!!rsp?.json?.data?.bridgeOnline);
        }
        catch (e) {
            console.error('[AiChatBotEx] CodeBuddy health check failed', e);
            setCodebuddyOnline(false);
        }
    }, []);
    const fetchIssueAiGameWorkspaces = useCallback(async () => {
        if (!currentAppId) {
            setIssueAiGameWorkspaces([]);
            setIssueAiGameWorkspacesLoaded(true);
            return;
        }
        try {
            const rsp = await RestHelper.mustPost('/api/aiPilot/listIssueAiWorkspaces', {
                appId: currentAppId,
                appIds: [currentAppId],
            });
            setIssueAiGameWorkspaces(ensureCurrentAppIssueWorkspace(rsp?.json?.data?.workspaceList, currentAppId));
        }
        catch (e) {
            console.error('[AiChatBotEx] Failed to fetch Issue AI game workspaces', e);
            setIssueAiGameWorkspaces(ensureCurrentAppIssueWorkspace([], currentAppId));
        }
        finally {
            setIssueAiGameWorkspacesLoaded(true);
        }
    }, [currentAppId]);
    const fetchIssueAiHistorySessions = useCallback(async (appId, issueId) => {
        const targetAppId = appId || selectedIssueAiAppId;
        if (!targetAppId) {
            setIssueAiHistorySessions([]);
            return;
        }
        // 优先用显式参数；否则取当前详情页 URL；再回退 pending（如弹窗输入 Issue ID）
        const targetIssueId = issueId
            || parseIssueIdFromPathname(window.location.pathname)
            || aiDrawerSlice.pendingIssueAnalysisId
            || '';
        try {
            const payload = { appId: targetAppId };
            if (targetIssueId) {
                payload.issueId = targetIssueId;
            }
            const rsp = await RestHelper.mustPost('/api/aiPilot/listIssueAiHistorySessions', payload);
            setIssueAiHistorySessions(rsp?.json?.data?.sessionList || []);
        }
        catch (e) {
            console.error('[AiChatBotEx] Failed to fetch Issue AI history sessions', e);
            setIssueAiHistorySessions([]);
        }
    }, [selectedIssueAiAppId, aiDrawerSlice.pendingIssueAnalysisId]);
    useEffect(() => {
        fetchCrashClawWorkspaces();
    }, [fetchCrashClawWorkspaces]);
    useEffect(() => {
        fetchCodebuddyHealth();
        const timer = setInterval(fetchCodebuddyHealth, 30000);
        return () => clearInterval(timer);
    }, [fetchCodebuddyHealth]);
    useEffect(() => {
        fetchIssueAiGameWorkspaces();
    }, [fetchIssueAiGameWorkspaces]);
    useEffect(() => {
        if (activeProduct === 'ISSUE_AI' && selectedIssueAiAppId) {
            fetchIssueAiHistorySessions(selectedIssueAiAppId);
        }
    }, [activeProduct, selectedIssueAiAppId, fetchIssueAiHistorySessions]);
    // 从问题详情页点击"AI分析"按钮：自动切换到 Issue AI 工作区并加载对应 issue 的会话
    useEffect(() => {
        const pendingIssueId = aiDrawerSlice.pendingIssueAnalysisId;
        const pendingAppId = aiDrawerSlice.pendingIssueAnalysisAppId;
        if (!pendingIssueId || !pendingAppId)
            return;
        if (aiDrawerSlice.pendingCrashClawWorkspaceUid)
            return;
        // 先设占位值，阻止下面的 auto-select effect 误选其他 issue 的会话
        setSelectedIssueAiSessionId('__pending__');
        // 切到问题分析工作区
        const workspaceKey = makeIssueAiGameWorkspaceSelection(pendingAppId);
        setUnifiedWorkspaceSelection(workspaceKey);
        // 有该 issue 的历史会话 → 打开其最新一条；无历史才 forceNew
        const findAndSelect = async () => {
            try {
                const rsp = await RestHelper.mustPost('/api/aiPilot/listIssueAiHistorySessions', {
                    appId: pendingAppId,
                    issueId: pendingIssueId,
                });
                // 后端按 appId 全量返回该游戏下的在线分析会话（含所有 issue），
                // 这里再按当前 issue 过滤，选中「对应 issue 下最新的一条」历史会话；
                // 该 issue 无历史则进入新建分析。
                const sessionList = rsp?.json?.data?.sessionList || [];
                setIssueAiHistorySessions(sessionList);
                // issueId 在 ES / DB / URL 之间大小写不一致（移动端多为大写，PC 端多为小写），
                // MySQL 是大小写不敏感的，JS 严格相等不是，这里统一按小写比较，避免漏匹配后误建新会话。
                // CrashClaw 与 Dify 共表后，这里只认 Dify 会话，否则会把 CrashClaw 报告在问题分析 UI 里打开。
                const matched = sessionList.filter((s) => (s.issueId || '').toLowerCase() === pendingIssueId.toLowerCase()
                    && !AiAnalysisType.isCrashClaw(s.analysisType));
                if (matched.length > 0) {
                    const latest = matched[0];
                    setSelectedIssueAiSessionId(makeIssueAiSessionId(latest.issueId, latest.conversationId));
                }
                else {
                    setSelectedIssueAiSessionId(makeIssueAiSessionId(pendingIssueId, ISSUE_AI_CONVERSATION_NEW));
                }
            }
            catch (e) {
                console.error('[AiChatBotEx] Failed to resolve Issue AI session for pending analysis', e);
                setSelectedIssueAiSessionId(makeIssueAiSessionId(pendingIssueId, ISSUE_AI_CONVERSATION_NEW));
            }
        };
        findAndSelect();
        // 消费后清空，避免后续页面切换仍按旧 issueId 过滤历史
        useZuStore.setState((state) => ({
            ...state,
            aiDrawer: {
                ...state.aiDrawer,
                pendingIssueAnalysisId: '',
                pendingIssueAnalysisAppId: '',
            },
        }));
    }, [aiDrawerSlice.pendingIssueAnalysisId, aiDrawerSlice.pendingIssueAnalysisAppId, aiDrawerSlice.pendingCrashClawWorkspaceUid]);
    // 详情页「AI 分析」CrashClaw 分流：切到指定工作区。校正 effect 在 pending 未消费前必须让路。
    useEffect(() => {
        const pendingUid = aiDrawerSlice.pendingCrashClawWorkspaceUid;
        if (!pendingUid)
            return;
        setUnifiedWorkspaceSelection(pendingUid);
    }, [aiDrawerSlice.pendingCrashClawWorkspaceUid, aiDrawerSlice.pendingCrashClawSeq]);
    // 面板只消费「要打开哪条会话 / 自动发什么」，workspaceUid 要留到列表校正安全后再清，
    // 否则 consume 后校正 effect 可能把指定工作区打回 ai_agent。
    const consumePendingCrashClawIntent = useCallback(() => {
        useZuStore.setState((state) => ({
            ...state,
            aiDrawer: {
                ...state.aiDrawer,
                pendingCrashClawSessionId: '',
                pendingCrashClawAutoSendText: '',
                pendingCrashClawIssueAppId: '',
                pendingCrashClawIssueId: '',
            },
        }));
    }, []);
    // 进入问题分析工作区时，自动选中最新一条历史会话
    useEffect(() => {
        if (activeProduct === 'ISSUE_AI' && issueAiHistorySessions.length > 0 && !selectedIssueAiSessionId) {
            const latest = issueAiHistorySessions.find((s) => !AiAnalysisType.isCrashClaw(s.analysisType));
            if (!latest)
                return;
            setSelectedIssueAiSessionId(makeIssueAiSessionId(latest.issueId, latest.conversationId));
        }
    }, [activeProduct, issueAiHistorySessions, selectedIssueAiSessionId, selectedIssueAiAppId]);
    useEffect(() => {
        if (crashClawWorkspacesRefreshTrigger > 0) {
            fetchCrashClawWorkspaces();
            fetchIssueAiGameWorkspaces();
        }
    }, [crashClawWorkspacesRefreshTrigger, fetchCrashClawWorkspaces, fetchIssueAiGameWorkspaces]);
    // 工作区选择变化时同步 localStorage
    useEffect(() => {
        const normalized = normalizeWorkspaceSelection(unifiedWorkspaceSelection);
        if (normalized !== unifiedWorkspaceSelection) {
            setUnifiedWorkspaceSelection(normalized);
            return;
        }
        patchStorageData({ workspaceSelection: normalized });
    }, [unifiedWorkspaceSelection]);
    // 没有 RTX 时不要回到 CrashSight AI（Knot 会直接拒绝）。有当前项目就去问题分析。
    const domesticFallbackSelection = canUseKnotAssistant === false && currentAppId
        ? makeIssueAiGameWorkspaceSelection(currentAppId)
        : 'ai_agent';
    // 如果当前选中 codebuddy 但 Global Bridge 离线，自动回退（海外由下方收敛 effect 处理）
    useEffect(() => {
        if (!EnvUtil.isChinaEnv())
            return;
        if (unifiedWorkspaceSelection === 'codebuddy' && !codebuddyOnline) {
            setUnifiedWorkspaceSelection(domesticFallbackSelection);
        }
    }, [unifiedWorkspaceSelection, codebuddyOnline, domesticFallbackSelection]);
    // CrashClaw 工作区列表拉取完成后校正选择（只校验 CrashClaw 类型选择；海外不回退到 ai_agent）。
    useEffect(() => {
        if (!EnvUtil.isChinaEnv())
            return;
        if (!crashClawWorkspacesLoaded)
            return;
        if (!isCrashClawWorkspace(unifiedWorkspaceSelection))
            return;
        // pending 分流期间让路：列表还没回来时不要把指定工作区打回 ai_agent
        if (aiDrawerSlice.pendingCrashClawWorkspaceUid)
            return;
        if (crashClawWorkspaces.length === 0) {
            setUnifiedWorkspaceSelection(domesticFallbackSelection);
            return;
        }
        const stillExists = crashClawWorkspaces.some(ws => ws.workspaceUid === unifiedWorkspaceSelection);
        if (!stillExists) {
            setUnifiedWorkspaceSelection(domesticFallbackSelection);
        }
    }, [crashClawWorkspaces, unifiedWorkspaceSelection, crashClawWorkspacesLoaded, aiDrawerSlice.pendingCrashClawWorkspaceUid, domesticFallbackSelection]);
    // 选择已落到指定工作区、且列表里能看到它之后，再放开校正 effect。
    // 必须等 session/autoSend 被面板消费完：过早清 uid 会让面板的 issue 分析 effect 直接 return。
    useEffect(() => {
        const uid = aiDrawerSlice.pendingCrashClawWorkspaceUid;
        if (!uid)
            return;
        if (aiDrawerSlice.pendingCrashClawSessionId || aiDrawerSlice.pendingCrashClawAutoSendText)
            return;
        if (unifiedWorkspaceSelection !== uid)
            return;
        if (!crashClawWorkspacesLoaded)
            return;
        if (!crashClawWorkspaces.some((ws) => ws.workspaceUid === uid))
            return;
        useZuStore.setState((state) => ({
            ...state,
            aiDrawer: {
                ...state.aiDrawer,
                pendingCrashClawWorkspaceUid: '',
            },
        }));
    }, [
        aiDrawerSlice.pendingCrashClawWorkspaceUid,
        aiDrawerSlice.pendingCrashClawSessionId,
        aiDrawerSlice.pendingCrashClawAutoSendText,
        unifiedWorkspaceSelection,
        crashClawWorkspacesLoaded,
        crashClawWorkspaces,
    ]);
    // 问题 AI 游戏工作区列表拉取完成后校正选择（国内失效回退 ai_agent；海外交给收敛 effect）。
    useEffect(() => {
        if (!EnvUtil.isChinaEnv())
            return;
        if (!issueAiGameWorkspacesLoaded)
            return;
        if (!isIssueAiWorkspace(unifiedWorkspaceSelection))
            return;
        const appId = parseAppIdFromIssueAiWorkspace(unifiedWorkspaceSelection);
        const stillExists = issueAiGameWorkspaces.some((x) => x.appId === appId);
        if (!stillExists) {
            setUnifiedWorkspaceSelection(domesticFallbackSelection);
        }
    }, [issueAiGameWorkspaces, unifiedWorkspaceSelection, issueAiGameWorkspacesLoaded, domesticFallbackSelection]);
    // 外部用户本地记住了 CrashSight AI 时，打开抽屉改到当前项目的问题分析。
    useEffect(() => {
        if (!EnvUtil.isChinaEnv())
            return;
        if (canUseKnotAssistant !== false)
            return;
        if (!currentAppId)
            return;
        if (!isAiAgentWorkspace(unifiedWorkspaceSelection))
            return;
        setUnifiedWorkspaceSelection(makeIssueAiGameWorkspaceSelection(currentAppId));
    }, [canUseKnotAssistant, currentAppId, unifiedWorkspaceSelection]);
    // 海外：抽屉内只保留问题分析工作区。非 issue_ai_app 或失效的 issue 选择 → 收敛到第一个可用问题分析工作区（有则切，无则保持，由内容区空态承接）。
    useEffect(() => {
        if (EnvUtil.isChinaEnv())
            return;
        if (!issueAiGameWorkspacesLoaded)
            return;
        if (isIssueAiWorkspace(unifiedWorkspaceSelection)) {
            const appId = parseAppIdFromIssueAiWorkspace(unifiedWorkspaceSelection);
            if (issueAiGameWorkspaces.some((x) => x.appId === appId))
                return;
            const first = issueAiGameWorkspaces[0];
            if (first?.appId) {
                setUnifiedWorkspaceSelection(makeIssueAiGameWorkspaceSelection(first.appId));
            }
            return;
        }
        const first = issueAiGameWorkspaces[0];
        if (first?.appId) {
            setUnifiedWorkspaceSelection(makeIssueAiGameWorkspaceSelection(first.appId));
        }
    }, [unifiedWorkspaceSelection, issueAiGameWorkspaces, issueAiGameWorkspacesLoaded]);
    // ============================================================
    // 内外部客户判定（需求点 2：CrashClaw 卡片仅对内部客户可见）
    // 沿用 CrashClaw 配置 Tab 的灰度规则：超管 或 RTX 白名单用户可见
    // ============================================================
    const isCrashClawCardVisible = useSelector((state) => {
        try {
            const current = state.user.get('current');
            if (!current)
                return false;
            const isSuperAdmin = !!current.get('isSuper');
            const isInCrashClawWhitelist = !!current.get('isInCrashClawWhitelist');
            return isSuperAdmin || isInCrashClawWhitelist;
        }
        catch {
            return false;
        }
    });
    /** 点击 CrashClaw 场景卡片：进入 CrashClaw 模式（选择首个工作区），无工作区则跳转配置页 */
    const onSelectCrashClawCard = useCallback(() => {
        if (crashClawWorkspaces.length > 0) {
            // 优先沿用当前已选的 CrashClaw 工作区，否则取首个
            const currentSelectionIsCrashClaw = isCrashClawWorkspace(unifiedWorkspaceSelection)
                && crashClawWorkspaces.some(ws => ws.workspaceUid === unifiedWorkspaceSelection);
            const targetUid = currentSelectionIsCrashClaw
                ? unifiedWorkspaceSelection
                : crashClawWorkspaces[0].workspaceUid;
            setUnifiedWorkspaceSelection(targetUid);
        }
        else {
            // 无工作区：直接跳配置页引导用户先创建
            window.open(buildCrashClawConfigUrl(), '_blank');
        }
    }, [crashClawWorkspaces, unifiedWorkspaceSelection]);
    /** CrashClaw 卡片「设置」按钮：跳转 CrashClaw 配置页 */
    const onClickCrashClawCardSetting = useCallback((e) => {
        e.stopPropagation();
        window.open(buildCrashClawConfigUrl(), '_blank');
    }, []);
    // ============================================================
    // 问题详情页关联入口（见 spec: 20260417_AI助手问题分析页面关联_spec.md）
    // ============================================================
    // --- CrashClaw 会话状态（从 CrashClawChatPanel 回传，用于全屏模式的侧边栏） ---
    const [clawConversationList, setClawConversationList] = useState([]);
    const [clawCurrentChatId, setClawCurrentChatId] = useState('');
    const [clawSelectChatId, setClawSelectChatId] = useState(null);
    const [clawSelectChatSeq, setClawSelectChatSeq] = useState(0);
    const [clawNewSessionSignal, setClawNewSessionSignal] = useState(0);
    const [clawRefreshHistorySignal, setClawRefreshHistorySignal] = useState(0);
    // CrashClaw 子场景状态：从 CrashClawChatPanel 提升上来，让 header 能与 AI Agent 一样
    // 在子场景下显示"返回"箭头按钮；点击通过 backToHomeSignal 通知子组件清空 intention。
    const [clawActiveIntention, setClawActiveIntention] = useState(null);
    const [clawBackToHomeSignal, setClawBackToHomeSignal] = useState(0);
    const [clawSelectedWorkspaceUid, setClawSelectedWorkspaceUid] = useState('');
    /** 打开抽屉时待恢复的 CrashClaw 会话 ID（来自 localStorage，消费后清空） */
    const [clawRestoreChatId, setClawRestoreChatId] = useState(null);
    // 统一选择器切换到 CrashClaw 工作区时，直接同步 clawSelectedWorkspaceUid（rename / 配置页等依赖）
    useEffect(() => {
        if (isCrashClawWorkspace(unifiedWorkspaceSelection)) {
            setClawSelectedWorkspaceUid(unifiedWorkspaceSelection);
        }
    }, [unifiedWorkspaceSelection]);
    const handleClawCurrentChatIdChange = useCallback((chatId) => {
        setClawCurrentChatId(chatId);
        if (chatId && isCrashClawWorkspace(unifiedWorkspaceSelection)) {
            patchStorageData({ clawChatId: chatId });
        }
    }, [unifiedWorkspaceSelection, patchStorageData]);
    const isInClawSubPage = activeProduct === 'CRASHCLAW' && clawActiveIntention !== null;
    // --- 历史会话 Popover 状态 ---
    const [historyPopoverOpen, setHistoryPopoverOpen] = useState(false);
    const [historySearchKeyword, setHistorySearchKeyword] = useState('');
    const [historyRenameTarget, setHistoryRenameTarget] = useState(null);
    const [historyRenameValue, setHistoryRenameValue] = useState('');
    const [historyRenaming, setHistoryRenaming] = useState(false);
    // 全屏按钮的 Tooltip 显隐状态：点击切全屏前主动关闭，避免残留闪动
    const [fullscreenTooltipOpen, setFullscreenTooltipOpen] = useState(false);
    const bumpClawHistoryRefresh = () => {
        setClawRefreshHistorySignal((s) => s + 1);
    };
    // ============================================================
    // 副作用
    // ============================================================
    // 切换到 CrashClaw Tab 或进入全屏侧边栏时，主动刷新历史列表
    useEffect(() => {
        if (activeProduct === 'CRASHCLAW') {
            bumpClawHistoryRefresh();
        }
    }, [activeProduct, chatBotFullscreen]);
    // 抽屉关闭时，中止正在进行的流式请求
    useEffect(() => {
        if (!aiDrawerSlice.visible && abortController) {
            abortController.abort();
            setAbortController(null);
            setIsAnswerOutputting(false);
        }
    }, [aiDrawerSlice.visible, abortController]);
    // 监听外部触发的"新会话"事件（来自 zustand store 的 startChatBotNewConversation）
    useEffect(() => {
        if (!aiDrawerSlice.chatBotNewConversationTrigger)
            return;
        patchStorageData({ conversationId: '' });
        setConversationId('');
        setChatHistoryList([]);
        setCurrentAnswerInfo(null);
        setCurrentThinkingInfo(null);
        setCurrentAnswerRandomId('');
        setIsAnswerOutputting(false);
        setActiveIntention(null);
    }, [aiDrawerSlice.chatBotNewConversationTrigger]);
    // chatBot 触发器：打开抽屉时按上次工作区恢复对应会话
    useEffect(() => {
        if (!aiDrawerSlice.chatBotTrigger)
            return;
        // CrashClaw 分流自己负责指定会话 / 新建，不要用 localStorage clawChatId 覆盖。
        // pending 不进 deps：消费 pending 时不要重跑本 effect，否则会把旧 clawChatId 灌进 restoreChatId。
        if (aiDrawerSlice.pendingCrashClawWorkspaceUid) {
            setClawRestoreChatId(null);
            return;
        }
        if (usesKnotAgentUI) {
            if (conversationId) {
                fetchChatMessages(conversationId);
                if (!getIsAnswerOutputting()) {
                    setCurrentAnswerInfo(null);
                }
            }
            fetchChatHistory();
            setClawRestoreChatId(null);
        }
        else if (activeProduct === 'CRASHCLAW') {
            const storedClawChatId = storageData?.clawChatId || '';
            setClawRestoreChatId(storedClawChatId || null);
            setClawRefreshHistorySignal((s) => s + 1);
        }
        else {
            setClawRestoreChatId(null);
            if (isIssueAiWorkspace(unifiedWorkspaceSelection)) {
                fetchIssueAiHistorySessions(parseAppIdFromIssueAiWorkspace(unifiedWorkspaceSelection));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiDrawerSlice.chatBotTrigger, activeProduct, fetchIssueAiHistorySessions, unifiedWorkspaceSelection]);
    // ============================================================
    // 计算属性
    // ============================================================
    // 对话轮次过多时显示警告（按用户提问次数计算，避免 tool call 等中间消息计入）
    const isTooManyRounds = chatHistoryList.filter((x) => !x.isBotNotUser).length > 50;
    // 合并历史消息 + 当前正在输出的思考内容 + 当前正在输出的 AI 回复
    const finalChatHistoryList = useMemo(() => {
        return [...chatHistoryList, currentThinkingInfo, currentAnswerInfo].filter((x) => !!x);
    }, [chatHistoryList, currentThinkingInfo, currentAnswerInfo]);
    const isEmptyConversation = finalChatHistoryList.length === 0;
    const crashClawWorkspaceOptions = useMemo(() => {
        // 过滤掉 CodeBuddy 全局工作区（W-codebuddy-global），它由 Global Bridge 自动创建，
        // agentType='codebuddy'，不应出现在 CrashClaw 工作区列表中（有独立的 'codebuddy' 静态入口）
        return crashClawWorkspaces
            .filter((x) => x.workspaceUid !== 'W-codebuddy-global')
            .map((x) => ({
            ...x,
            workspaceType: 'CRASHCLAW',
        }));
    }, [crashClawWorkspaces]);
    const issueAiGameWorkspaceOptions = useMemo(() => {
        return issueAiGameWorkspaces.map((x) => {
            const appId = x.appId;
            const appDisplayName = appIdToDisplayName[appId] || appId;
            return {
                workspaceUid: makeIssueAiGameWorkspaceSelection(appId),
                workspaceName: `${appDisplayName}-${ze('问题分析', 'Issue Analysis')}`,
                displayName: `${appDisplayName}-${ze('问题分析', 'Issue Analysis')}`,
                isOwner: true,
                ownerRtx: null,
                accessRole: null,
                healthStatus: 'HEALTHY',
                agentName: null,
                workspaceType: 'ISSUE_AI',
            };
        });
    }, [issueAiGameWorkspaces, appIdToDisplayName]);
    const issueAiHistoryList = useMemo(() => {
        const appDisplayName = appIdToDisplayName[selectedIssueAiAppId] || selectedIssueAiAppId || ze('游戏', 'Game');
        return issueAiHistorySessions
            .filter((x) => !AiAnalysisType.isCrashClaw(x.analysisType))
            .map((x) => {
            let tsMs = Date.now();
            if (typeof x.latestTime === 'number') {
                tsMs = x.latestTime;
            }
            else if (typeof x.latestTime === 'string') {
                const parsed = Date.parse(x.latestTime);
                if (Number.isFinite(parsed))
                    tsMs = parsed;
            }
            const issueName = x.issueTitle || issueIdToNameMap[x.issueId] || ze('未命名问题', 'Untitled Issue');
            return {
                id: makeIssueAiSessionId(x.issueId, x.conversationId),
                name: `${appDisplayName}-${issueName}-${x.issueId}`,
                updated_at: Math.floor(tsMs / 1000),
            };
        }).sort((a, b) => b.updated_at - a.updated_at);
    }, [issueAiHistorySessions, appIdToDisplayName, selectedIssueAiAppId, issueIdToNameMap]);
    /** 根据当前产品 Tab 选取对应的会话列表和当前会话 ID（复用全屏模式侧边栏的同一份数据） */
    const activeHistoryList = usesKnotAgentUI
        ? conversationList
        : activeProduct === 'CRASHCLAW'
            ? clawConversationList
            : issueAiHistoryList;
    const activeCurrentId = usesKnotAgentUI
        ? conversationId
        : activeProduct === 'CRASHCLAW'
            ? clawCurrentChatId
            : selectedIssueAiSessionId;
    const filteredActiveHistoryList = useMemo(() => filterConversationList(activeHistoryList, historySearchKeyword), [activeHistoryList, historySearchKeyword]);
    const renameClawConversation = useCallback(async (chatId, name) => {
        if (!clawSelectedWorkspaceUid) {
            throw new Error('workspace not selected');
        }
        await RestHelper.mustPost('/redir/api/crashclaw/updateCopawSession', {
            workspaceUid: clawSelectedWorkspaceUid,
            chatId,
            name: name.trim(),
        });
        setClawConversationList((prev) => prev.map((item) => (item.id === chatId ? { ...item, name: name.trim() } : item)));
        setClawRefreshHistorySignal((s) => s + 1);
    }, [clawSelectedWorkspaceUid]);
    /** 将会话列表按时间分组（今天/昨天/7天内/30天内/更早），与全屏侧边栏分组逻辑对齐 */
    const groupedHistoryList = useMemo(() => {
        const now = Date.now() / 1000;
        const oneDay = 24 * 60 * 60;
        const groups = [
            { title: ze('今天', 'Today'), max: oneDay, items: [] },
            { title: ze('昨天', 'Yesterday'), max: 2 * oneDay, items: [] },
            { title: ze('7天内', 'Within 7 days'), max: 7 * oneDay, items: [] },
            { title: ze('30天内', 'Within 30 days'), max: 30 * oneDay, items: [] },
            { title: ze('更早', 'Older'), max: Infinity, items: [] },
        ];
        for (const conv of filteredActiveHistoryList) {
            const diff = now - conv.updated_at;
            const group = groups.find(g => diff < g.max);
            group?.items.push(conv);
        }
        return groups.filter(g => g.items.length > 0);
    }, [filteredActiveHistoryList]);
    // ============================================================
    // 操作函数
    // ============================================================
    /** 停止当前正在进行的 SSE 流式对话 */
    function stopCurrentConversation() {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
        }
        setCurrentAnswerInfo(null);
        setCurrentThinkingInfo(null);
        setIsAnswerOutputting(false);
        setCurrentAnswerRandomId('');
    }
    /**
     * 将当前正在流式输出的文本回复（currentAnswerInfo）提交到 chatHistoryList，
     * 然后清空 currentAnswerInfo。
     * 用于 TEXT_MESSAGE_END 或 TOOL_CALL_START 到来时"截断"当前文本段，
     * 确保后续 tool call 不会覆盖已有的文本内容。
     */
    function commitCurrentAnswer() {
        const answer = getCurrentAnswerInfo();
        if (answer && answer.text.trim()) {
            setChatHistoryList([...getChatHistoryList(), answer]);
        }
        setCurrentAnswerInfo(null);
    }
    /**
     * 将当前正在流式输出的思考内容（currentThinkingInfo）提交到 chatHistoryList，
     * 标记 isStreaming=false 以触发 ThinkingPanel 自动折叠，然后清空 currentThinkingInfo。
     */
    function commitCurrentThinking() {
        const thinking = getCurrentThinkingInfo();
        if (thinking?.thinking && thinking.thinking.text.trim()) {
            const committed = {
                ...thinking,
                thinking: { ...thinking.thinking, isStreaming: false },
            };
            setChatHistoryList([...getChatHistoryList(), committed]);
        }
        setCurrentThinkingInfo(null);
    }
    /** 内部按钮触发的"开始新会话" */
    function handleStartNewConversation() {
        if (getIsAnswerOutputting()) {
            stopCurrentConversation();
        }
        setConversationId('');
        setChatHistoryList([]);
        setCurrentAnswerInfo(null);
        setIsAnswerOutputting(false);
        patchStorageData({ conversationId: '' });
        setActiveIntention(null);
        message.success(ze('已开始新会话', 'New conversation started'));
    }
    /** 关闭 AI 抽屉 */
    function onClickClose() {
        aiDrawerSlice.setVisible(false);
    }
    /** 切换全屏 / 退出全屏模式 */
    function onToggleFullscreen() {
        // 切换前先把全屏按钮的 Tooltip 强制关闭，避免切换瞬间 DOM 位置剧变
        // 导致 antd Tooltip 的 popup 残留在原位置闪动（mouseleave 收不到）。
        setFullscreenTooltipOpen(false);
        aiDrawerSlice.setChatBotFullscreen(!chatBotFullscreen);
    }
    /**
     * 通过 zustand store 触发新会话，支持外部调用
     * 区别于 handleStartNewConversation：这里通过 store action 触发，
     * 会被 chatBotNewConversationTrigger 的 useEffect 监听到
     */
    function onClickNewConversation() {
        aiDrawerSlice.startChatBotNewConversation();
    }
    /**
     * 从会话列表选择一个历史会话
     * 通过 /api/knotAgent/chatMessages 获取该会话的消息
     */
    function onSelectConversation(selectedConversationId) {
        if (getIsAnswerOutputting()) {
            stopCurrentConversation();
        }
        setConversationId(selectedConversationId);
        fetchChatMessages(selectedConversationId);
        setCurrentAnswerInfo(null);
        setActiveIntention(null);
    }
    /** 点击场景卡片，进入对应场景子页面 */
    function onSelectScenario(intention) {
        setActiveIntention(intention);
    }
    /** 从场景子页面返回首页 */
    function onBackToHome() {
        setActiveIntention(null);
    }
    // ============================================================
    // 问题详情页关联入口相关处理函数
    // ============================================================
    /**
     * CrashSight AI 工作区统一发送入口（Knot Agent 链路）。
     * CrashClaw 工作区由 CrashClawChatPanel 内 sendMessage 处理，不会进入此函数。
     */
    function handleSend(text) {
        if (!usesKnotAgentUI) {
            return;
        }
        let convId = conversationId;
        // CODEBUDDY 的会话号由前端生成（cb- 前缀，与 bridge 的 session 别名约定一致），
        // 首轮无会话时新建，并标记需要在 RUN_FINISHED 后刷新历史侧栏。
        if (isCodeBuddyWorkspace && !convId) {
            convId = `cb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            setConversationId(convId);
            setPrepareToRefreshConversationList(true);
        }
        sendChatMessage(convId, text);
    }
    /**
     * 处理附件发送：将文件转 base64 后连同文本一起发送（对齐 CrashClawChatPanel 逻辑）。
     */
    async function handleAttach(files, text) {
        if (!usesKnotAgentUI)
            return;
        let convId = conversationId;
        if (isCodeBuddyWorkspace && !convId) {
            convId = `cb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            setConversationId(convId);
            setPrepareToRefreshConversationList(true);
        }
        const fileDescriptions = files.map((f) => {
            const sizeStr = f.size > 1024 * 1024
                ? `${(f.size / 1024 / 1024).toFixed(1)} MB`
                : `${(f.size / 1024).toFixed(0)} KB`;
            return `[用户发送文件: ${f.name} (${sizeStr})]`;
        }).join('\n');
        const fullText = text.trim()
            ? `${fileDescriptions}\n\n${text.trim()}`
            : fileDescriptions;
        const base64Files = await Promise.all(files.map(async (f) => {
            const b64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(f);
            });
            return { name: f.name, mimeType: f.type, data: b64 };
        }));
        sendChatMessage(convId, fullText, base64Files);
    }
    /** 从历史会话 Popover 中选择一条会话，根据当前产品 Tab 分发到对应的加载逻辑 */
    function onHistorySelect(id) {
        if (usesKnotAgentUI) {
            onSelectConversation(id);
        }
        else if (activeProduct === 'CRASHCLAW') {
            setClawSelectChatId(id);
            setClawSelectChatSeq((s) => s + 1);
        }
        else {
            setSelectedIssueAiSessionId(id);
        }
        setHistoryPopoverOpen(false);
        setHistorySearchKeyword('');
    }
    function onSelectClawConversation(chatId) {
        setClawSelectChatId(chatId);
        setClawSelectChatSeq((s) => s + 1);
    }
    function onClawNewConversation() {
        setClawSelectChatId(null);
        setClawNewSessionSignal((s) => s + 1);
        patchStorageData({ clawChatId: '' });
    }
    async function handleHistoryRenameOk() {
        if (!historyRenameTarget)
            return;
        const nextName = historyRenameValue.trim();
        if (!nextName) {
            message.warning(ze('会话名称不能为空', 'Session name cannot be empty'));
            return;
        }
        setHistoryRenaming(true);
        try {
            await renameClawConversation(historyRenameTarget.id, nextName);
            setHistoryRenameTarget(null);
            setHistoryRenameValue('');
            message.success(ze('已重命名', 'Renamed'));
        }
        catch (e) {
            message.error(ze('重命名失败，请重试', 'Rename failed, please retry'));
        }
        finally {
            setHistoryRenaming(false);
        }
    }
    // handleGoToCrashClawConfig 已迁移到 CrashClawChatPanel 组件
    // ============================================================
    // AG-UI SSE 消息处理
    // ============================================================
    /**
     * SSE onmessage 回调：解析 AG-UI 协议事件并更新对话状态
     *
     * 核心处理逻辑：
     * - RUN_STARTED: 从 rawEvent 提取 conversationId（新会话时需要）
     * - TEXT_MESSAGE_START: 标记 AI 回复开始，提取 messageId
     * - TEXT_MESSAGE_CONTENT: 累积 delta 文本到 currentAnswerInfo
     * - TEXT_MESSAGE_END: 提交当前文本段到 chatHistoryList（为后续 tool call 腾位）
     * - THINKING_TEXT_MESSAGE_START: 创建思考条目，开始流式累积思考文本
     * - THINKING_TEXT_MESSAGE_CONTENT: 流式累积思考文本 delta
     * - THINKING_TEXT_MESSAGE_END: 提交思考条目到 chatHistoryList（自动折叠）
     * - TOOL_CALL_START: 创建 tool call 项，追加到 chatHistoryList
     * - TOOL_CALL_ARGS: 流式累积工具参数 delta 到当前 tool call 项
     * - TOOL_CALL_END: 标记工具调用完成（CrashClaw 模式下 rawEvent 可能携带 output）
     * - TOOL_CALL_RESULT: 将工具执行结果 attach 到对应的 tool call 项
     * - RUN_FINISHED: 整个对话结束，刷新会话列表
     * - RUN_ERROR: 错误处理
     *
     * @param randomId - 本次请求的随机 ID，用于过滤非当前请求的消息
     * @param e - SSE 事件对象，e.data 为 JSON 字符串
     */
    function onReceiveEventSourceMessage(randomId, e) {
        if (!e.data)
            return;
        if (getCurrentAnswerRandomId() !== randomId)
            return;
        const agEvent = parseAgUiEvent(e.data);
        if (!agEvent)
            return;
        const eventConversationId = extractConversationId(agEvent);
        if (!getConversationId() && eventConversationId) {
            setConversationId(eventConversationId);
            setPrepareToRefreshConversationList(true);
        }
        switch (agEvent.type) {
            case AgUiEventType.TEXT_MESSAGE_START: {
                const msgId = extractMessageId(agEvent);
                const answerInfo = {
                    ...(getCurrentAnswerInfo() || {
                        isBotNotUser: true,
                        text: '',
                        isLoading: true,
                    }),
                };
                if (msgId) {
                    answerInfo.messageId = msgId;
                }
                setCurrentAnswerInfo(answerInfo);
                break;
            }
            case AgUiEventType.TEXT_MESSAGE_CONTENT: {
                const contentEvent = agEvent;
                const answerInfo = {
                    ...(getCurrentAnswerInfo() || {
                        isBotNotUser: true,
                        text: '',
                        isLoading: true,
                    }),
                };
                answerInfo.text += contentEvent.delta || '';
                answerInfo.isLoading = false;
                const msgId = extractMessageId(agEvent);
                if (msgId) {
                    answerInfo.messageId = msgId;
                }
                setCurrentAnswerInfo(answerInfo);
                break;
            }
            case AgUiEventType.TEXT_MESSAGE_END: {
                commitCurrentAnswer();
                break;
            }
            // ---- Thinking 思考过程事件处理（AG-UI deprecated THINKING_* 系列） ----
            case AgUiEventType.THINKING_TEXT_MESSAGE_START: {
                // 新一轮思考开始：先提交可能残留的文本段，然后创建 thinking 条目
                commitCurrentAnswer();
                setCurrentThinkingInfo({
                    isBotNotUser: true,
                    text: '',
                    thinking: { text: '', isStreaming: true },
                });
                break;
            }
            case AgUiEventType.THINKING_TEXT_MESSAGE_CONTENT: {
                const thinkEvent = agEvent;
                const delta = thinkEvent.delta || '';
                if (!delta)
                    break;
                const current = getCurrentThinkingInfo();
                const prevText = current?.thinking?.text || '';
                setCurrentThinkingInfo({
                    isBotNotUser: true,
                    text: '',
                    thinking: { text: prevText + delta, isStreaming: true },
                });
                break;
            }
            case AgUiEventType.THINKING_TEXT_MESSAGE_END: {
                // 思考结束：提交到历史列表（isStreaming=false 触发 ThinkingPanel 自动折叠）
                commitCurrentThinking();
                break;
            }
            // ---- Tool Call 事件处理 ----
            case AgUiEventType.TOOL_CALL_START: {
                const tcEvent = agEvent;
                // 先提交可能尚未结束的文本段
                commitCurrentAnswer();
                setChatHistoryList(upsertToolCallItem(getChatHistoryList(), {
                    name: tcEvent.toolCallName || 'tool',
                    callId: tcEvent.toolCallId,
                    arguments: tcEvent.rawEvent?.arguments,
                }));
                break;
            }
            case AgUiEventType.TOOL_CALL_ARGS: {
                // 流式累积工具参数 delta：找到最近一条 tool call 项，拼接 delta
                const argsEvent = agEvent;
                const delta = argsEvent.delta || '';
                if (!delta)
                    break;
                const list = getChatHistoryList();
                // 从末尾向前找到匹配 callId 的 tool call 项（通常就是最后一条）
                for (let i = list.length - 1; i >= 0; i--) {
                    const tc = list[i].toolCall;
                    if (tc && (!argsEvent.toolCallId || tc.callId === argsEvent.toolCallId)) {
                        const updated = [...list];
                        updated[i] = {
                            ...updated[i],
                            toolCall: {
                                ...tc,
                                arguments: (tc.arguments || '') + delta,
                            },
                        };
                        setChatHistoryList(updated);
                        break;
                    }
                }
                break;
            }
            case AgUiEventType.TOOL_CALL_END: {
                const tcEnd = agEvent;
                const output = tcEnd.rawEvent?.output;
                if (tcEnd.toolCallId) {
                    const list = getChatHistoryList();
                    if (findPendingToolCallIndex(list, tcEnd.toolCallId) < 0) {
                        setChatHistoryList(upsertToolCallItem(list, {
                            name: tcEnd.toolCallName || 'tool',
                            callId: tcEnd.toolCallId,
                            output: output || undefined,
                            completed: true,
                        }));
                    }
                    else {
                        setChatHistoryList(markToolCallCompleted(list, tcEnd.toolCallId, output || undefined));
                    }
                }
                break;
            }
            case AgUiEventType.TOOL_CALL_RESULT: {
                // 工具执行结果：实际数据在 content 字段（字符串），rawEvent.result 不可靠（可能是空对象 {}）
                const resultEvent = agEvent;
                const callId = resultEvent.toolCallId;
                // 优先取 content（后端实际结果字段），回退到 rawEvent.result 并确保是字符串
                let output = resultEvent.content;
                if (!output && resultEvent.rawEvent?.result != null) {
                    const raw = resultEvent.rawEvent.result;
                    output = typeof raw === 'string' ? raw : JSON.stringify(raw);
                }
                if (callId) {
                    setChatHistoryList(markToolCallCompleted(getChatHistoryList(), callId, output || undefined));
                }
                break;
            }
            case AgUiEventType.RUN_FINISHED: {
                commitCurrentThinking();
                commitCurrentAnswer();
                setChatHistoryList(markAllToolCallsCompleted(getChatHistoryList()));
                setIsAnswerOutputting(false);
                if (getPrepareToRefreshConversationList()) {
                    setPrepareToRefreshConversationList(false);
                    fetchChatHistory();
                }
                break;
            }
            case AgUiEventType.RUN_ERROR: {
                setCurrentAnswerInfo(null);
                setCurrentThinkingInfo(null);
                setIsAnswerOutputting(false);
                message.error(ze('AI 分析出现错误，请重试', 'AI analysis error, please retry'));
                break;
            }
            default:
                break;
        }
    }
    // ============================================================
    // API 调用
    // ============================================================
    /**
     * 发送聊天消息
     *
     * 接口：POST /api/knotAgent/chat (SSE, AG-UI 协议)
     * 参数：{ message, stream, conversationId?, appId?, intention? }
     *
     * 流程：
     * 1. 将用户消息和之前的 AI 回复合并到历史记录
     * 2. 创建 AI 回复占位（loading 状态）
     * 3. 通过 SSE 接收 AG-UI 事件，累积文本到 currentAnswerInfo
     * 4. RUN_FINISHED 时标记完成
     *
     * 当用户处于某个场景子页面时，请求会携带 intention 参数标识当前场景
     */
    async function sendChatMessage(currentConversationId, queryText, attachedFiles) {
        if (getIsAnswerOutputting()) {
            stopCurrentConversation();
        }
        const newQueryItem = {
            isBotNotUser: false,
            text: queryText,
        };
        setChatHistoryList([...getChatHistoryList(), getCurrentAnswerInfo(), newQueryItem].filter((x) => !!x));
        const randomId = String(Math.random());
        setCurrentAnswerRandomId(randomId);
        setCurrentAnswerInfo({
            isBotNotUser: true,
            text: '',
            isLoading: true,
        });
        setIsAnswerOutputting(true);
        const ac = new AbortController();
        setAbortController(ac);
        // 按工作区切换接口与请求字段：
        // - AI_AGENT → /api/knotAgent/chat，会话字段 conversationId，支持 appId/intention
        // - CODEBUDDY → /redir/api/codeBuddy/chat，会话字段 sessionId（cb- 前缀）
        // 两者响应均为 AG-UI SSE，共用 onReceiveEventSourceMessage 解析。
        let url;
        const params = {
            message: queryText,
            stream: true,
        };
        if (isCodeBuddyWorkspace) {
            url = '/redir/api/codeBuddy/chat';
            if (currentConversationId) {
                params.sessionId = currentConversationId;
            }
            // 模型切换已隐藏，使用后端默认模型（与 v1 保持一致）
            // 附件：文件 base64 数据传给 Bridge
            if (attachedFiles && attachedFiles.length > 0) {
                params.attachedFiles = attachedFiles;
            }
        }
        else {
            url = '/api/knotAgent/chat';
            // 安全兜底：如果 conversationId 是 CodeBuddy 的 cb- 前缀 ID（共享状态残留），
            // 不能传给 Knot Agent，否则 Knot 找不到会话返回 400。
            if (currentConversationId && !currentConversationId.startsWith('cb-')) {
                params.conversationId = currentConversationId;
            }
            if (currentAppId) {
                params.appId = currentAppId;
            }
            if (activeIntention) {
                params.intention = activeIntention;
            }
        }
        const options = {
            signal: ac.signal,
            statusCodeToCallback: {
                500: () => {
                    message.error(t('api.服务接口出现错误'));
                },
            },
            onmessage: (e) => onReceiveEventSourceMessage(randomId, e),
        };
        try {
            await RestHelper.postEventStream(url, params, options);
            // SSE 流已正常关闭。如果此刻仍处于 outputting 状态，说明从未收到 RUN_FINISHED：
            // 后端（或更上游的 Knot）静默地空响应，loading 气泡会永远停在那里。
            // 此处兜底清理 + 提示，避免出现"发了消息一直没回复"的死状态。
            if (getIsAnswerOutputting()) {
                const answer = getCurrentAnswerInfo();
                const thinking = getCurrentThinkingInfo();
                const hasContent = (!!answer?.text && answer.text.trim() !== '') ||
                    (!!thinking?.thinking?.text && thinking.thinking.text.trim() !== '');
                if (hasContent) {
                    commitCurrentThinking();
                    commitCurrentAnswer();
                }
                else {
                    setCurrentAnswerInfo(null);
                    setCurrentThinkingInfo(null);
                    message.error(ze('AI 未返回内容，请重试', 'AI returned no content, please retry'));
                }
                setIsAnswerOutputting(false);
            }
        }
        catch (error) {
            if (error.name !== 'AbortError') {
                console.error('[AiChatBotEx] Chat message error:', error);
                setCurrentAnswerInfo(null);
                setCurrentThinkingInfo(null);
                setIsAnswerOutputting(false);
                message.error(ze('AI 对话请求失败，请检查网络或稍后重试', 'AI chat request failed, please retry later'));
            }
        }
        finally {
            setAbortController(null);
        }
    }
    /**
     * 获取会话列表（仅元信息，不含消息内容）
     *
     * 接口：POST /api/knotAgent/chatHistory  body: { page: 1, perPage: 100 }
     * 返回：data.conversations 为会话元信息数组（conversationId, title, createTimeMillis, updateTimeMillis）
     *
     * 消息内容需通过 fetchChatMessages 按 conversationId 单独获取
     */
    async function fetchChatHistory() {
        if (!EnvUtil.isChinaEnv()) {
            setConversationList([]);
            return;
        }
        try {
            // CODEBUDDY：/redir/api/codeBuddy/listSessions，返回 data[]{sessionId,title,createTime,updateTime}
            if (isCodeBuddyWorkspace) {
                const rsp = await RestHelper.post('/redir/api/codeBuddy/listSessions', {});
                const sessions = rsp?.json?.data || [];
                const transformedList = sessions
                    .map((s) => ({
                    id: s.sessionId,
                    name: s.title || ze('新会话', 'New Conversation'),
                    updated_at: s.updateTime ? moment(s.updateTime).unix() : 0,
                }))
                    .sort((a, b) => b.updated_at - a.updated_at);
                setConversationList(transformedList);
                return;
            }
            const rsp = await RestHelper.post('/api/knotAgent/chatHistory', { page: 1, perPage: 100 });
            const conversations = rsp?.json?.data?.conversations || [];
            const transformedList = transformConversationsToList(conversations);
            setConversationList(transformedList);
        }
        catch (error) {
            console.error('[AiChatBotEx] Failed to fetch chat history:', error);
        }
    }
    /**
     * 获取指定会话的消息列表
     *
     * 接口：POST /api/knotAgent/chatMessages  body: { conversationId }
     * 返回：data 为 KnotAgentMessage[] 数组
     *
     * 与旧版 AiChatBot 的 fetchChatMessages 对齐，按 conversationId 单独拉取消息
     */
    async function fetchChatMessages(targetConversationId) {
        if (!targetConversationId || !EnvUtil.isChinaEnv())
            return;
        try {
            // CODEBUDDY：/redir/api/codeBuddy/getSessionMessages，
            // 返回 data[]{userMessage, assistantMessage}，每条拆成用户 + AI 两个气泡。
            if (isCodeBuddyWorkspace) {
                const rsp = await RestHelper.post('/redir/api/codeBuddy/getSessionMessages', { sessionId: targetConversationId });
                const data = rsp?.json?.data || [];
                const historyList = [];
                for (const m of data) {
                    if (m.userMessage)
                        historyList.push({ isBotNotUser: false, text: m.userMessage });
                    if (m.assistantMessage)
                        historyList.push({ isBotNotUser: true, text: m.assistantMessage });
                }
                if (historyList.length > 0) {
                    setChatHistoryList(historyList);
                }
                else {
                    setConversationId('');
                }
                return;
            }
            const rsp = await RestHelper.post('/api/knotAgent/chatMessages', { conversationId: targetConversationId });
            const messages = rsp?.json?.data || [];
            const historyList = transformMessagesToHistoryList(messages);
            if (historyList.length > 0) {
                setChatHistoryList(historyList);
            }
            else {
                setConversationId('');
                patchStorageData({ conversationId: '' });
            }
        }
        catch (error) {
            console.error('[AiChatBotEx] Failed to fetch chat messages:', error);
            setConversationId('');
            patchStorageData({ conversationId: '' });
        }
    }
    // ============================================================
    // 渲染
    // ============================================================
    // 当前激活的场景配置
    const activeScenario = activeIntention
        ? SCENARIO_CARDS.find((c) => c.intention === activeIntention) || null
        : null;
    // 是否处于场景子页面（空会话 + 有选中场景）
    const isInScenarioSubPage = isEmptyConversation && activeScenario !== null;
    // 是否处于首页（空会话 + 无选中场景）
    const isOnHomePage = isEmptyConversation && activeScenario === null;
    // 对话气泡列表：thinking 项渲染为折叠思考面板，tool call 项渲染为工具折叠面板，其余渲染为聊天气泡
    const bubbleDomList = finalChatHistoryList.map((item, i) => {
        if (item.thinking) {
            return _jsx(ThinkingPanel, { thinking: item.thinking }, i);
        }
        if (item.toolCall) {
            return _jsx(ToolCallPanel, { toolCall: item.toolCall }, i);
        }
        return (_jsx(AiChatBotBubble, { messageId: item.messageId, conversationId: conversationId, isBotNotUser: !!item.isBotNotUser, text: item.text, isLoading: !!item.isLoading, messageTimeInfo: undefined, childElement: undefined, customContentTextRenderer: undefined }, i));
    });
    /** 历史会话 Popover 内容：按时间分组展示，与全屏模式侧边栏内容对齐 */
    const historyPopoverContent = (_jsxs("div", { className: scss.historyPopover, children: [_jsx("div", { className: scss.historyPopoverSearchWrap, children: _jsx(SearchBox, { placeholder: ze('搜索会话', 'Search conversations'), defaultSearch: historySearchKeyword, handleSearchInput: setHistorySearchKeyword, style: {
                        search_box: scss.conversationSearchBox,
                        ui_search: scss.conversationSearchUi,
                    } }) }), groupedHistoryList.length === 0 ? (_jsx("div", { className: scss.historyPopoverEmpty, children: historySearchKeyword.trim()
                    ? ze('没有匹配的会话', 'No matching conversations')
                    : ze('暂无历史会话', 'No conversation history') })) : (groupedHistoryList.map((group, i) => (_jsxs("div", { className: scss.historyPopoverGroup, children: [_jsx("div", { className: scss.historyPopoverGroupTitle, children: group.title }), group.items.map(conv => (_jsxs("div", { className: `${scss.historyPopoverItem} ${conv.id === activeCurrentId ? scss.historyPopoverItemActive : ''}`, onClick: () => onHistorySelect(conv.id), title: conv.name, children: [_jsx("span", { className: scss.historyPopoverItemText, children: conv.name }), activeProduct === 'CRASHCLAW' && (_jsx(EditOutlined, { className: scss.conversationRenameBtn, onClick: (e) => {
                                    e.stopPropagation();
                                    setHistoryRenameTarget(conv);
                                    setHistoryRenameValue(conv.name || '');
                                } }))] }, conv.id)))] }, i))))] }));
    /**
     * 顶部 Header
     * - 首页：标题 + 全屏/新会话/历史会话/关闭按钮
     * - 场景子页面：返回箭头 + 标题 + 关闭按钮
     * - 全屏模式下标题、新会话、历史会话按钮移到左侧 ConversationList 组件中
     */
    const getHeaderPopupContainer = (triggerNode) => triggerNode.parentElement || document.body;
    const headerDom = (_jsxs("div", { className: scss.header, children: [usesKnotAgentUI && isInScenarioSubPage && (_jsx(LeftOutlined, { style: { cursor: 'pointer', marginRight: '12px', fontSize: '14px' }, onClick: onBackToHome })), isInClawSubPage && (_jsx(LeftOutlined, { style: { cursor: 'pointer', marginRight: '12px', fontSize: '14px' }, onClick: () => setClawBackToHomeSignal((s) => s + 1) })), !chatBotFullscreen && (_jsx("div", { className: scss.headerTitle, children: "CrashSight AI" })), _jsx("div", { style: { flexGrow: 1 } }), _jsx(Tooltip, { title: chatBotFullscreen ? ze('退出全屏', 'Exit Fullscreen') : ze('全屏显示', 'Fullscreen'), getPopupContainer: getHeaderPopupContainer, visible: fullscreenTooltipOpen, onVisibleChange: setFullscreenTooltipOpen, mouseLeaveDelay: 0, children: _jsx("span", { className: scss.headerActionIcon, style: { marginRight: '12px' }, onClick: onToggleFullscreen, children: chatBotFullscreen ? _jsx(CompressOutlined, {}) : _jsx(ExpandOutlined, {}) }) }), !chatBotFullscreen && (_jsx(Tooltip, { title: ze('新会话', 'New Conversation'), getPopupContainer: getHeaderPopupContainer, children: _jsx(PlusOutlined, { className: scss.headerActionIcon, style: { marginRight: '12px' }, onClick: usesKnotAgentUI ? onClickNewConversation :
                        activeProduct === 'ISSUE_AI' ? onIssueAiNewSession :
                            () => setClawNewSessionSignal((s) => s + 1) }) })), !chatBotFullscreen && (_jsx(Popover, { content: historyPopoverContent, trigger: "click", open: historyPopoverOpen, onOpenChange: (open) => {
                    setHistoryPopoverOpen(open);
                    if (!open) {
                        setHistorySearchKeyword('');
                    }
                    if (open && activeProduct === 'CRASHCLAW') {
                        setClawRefreshHistorySignal((s) => s + 1);
                    }
                    if (open && activeProduct === 'ISSUE_AI') {
                        fetchIssueAiHistorySessions(selectedIssueAiAppId);
                    }
                }, placement: "bottomRight", overlayStyle: { width: 280 }, getPopupContainer: getHeaderPopupContainer, children: _jsx(Tooltip, { title: ze('历史会话', 'Conversation History'), open: historyPopoverOpen ? false : undefined, getPopupContainer: getHeaderPopupContainer, children: _jsx(ClockCircleOutlined, { className: scss.headerActionIcon, style: { marginRight: '12px' } }) }) })), _jsx(Tooltip, { title: ze('关闭', 'Close'), getPopupContainer: getHeaderPopupContainer, children: _jsx(CloseOutlined, { className: scss.headerActionIcon, onClick: onClickClose }) })] }));
    /** 统一工作区选择：CrashSight AI 与 CrashClaw 统一作为"工作区"入口。 */
    const handleSelectUnifiedWorkspace = useCallback((selection) => {
        const normalized = normalizeWorkspaceSelection(selection);
        setUnifiedWorkspaceSelection(normalized);
        setWsPopoverOpen(false);
        if (normalized === 'codebuddy') {
            // CODEBUDDY 复用 AI_AGENT 聊天 UI；会话重置与历史拉取由 activeProduct 切换的 effect 统一处理
            return;
        }
        if (isAiAgentWorkspace(normalized)) {
            setActiveIntention(null);
            fetchChatHistory();
            return;
        }
        if (isIssueAiWorkspace(normalized)) {
            setActiveIntention(null);
            // 延迟自动选中最新会话：fetchIssueAiHistorySessions 完成后由 issueAiHistorySessions 的 useEffect 触发
            setSelectedIssueAiSessionId('');
            fetchIssueAiHistorySessions(parseAppIdFromIssueAiWorkspace(normalized));
            return;
        }
        if (isCrashClawWorkspace(normalized)) {
            setClawRefreshHistorySignal((s) => s + 1);
        }
    }, []);
    function onSelectIssueAiSession(sessionId) {
        setSelectedIssueAiSessionId(sessionId);
    }
    function onClearIssueAiSession() {
        setSelectedIssueAiSessionId('');
    }
    /** 问题分析工作区新建会话：弹出窗口让用户输入 issueId */
    function onIssueAiNewSession() {
        setIssueAiNewSessionIssueId('');
        setIssueAiNewSessionModalOpen(true);
    }
    function handleIssueAiNewSessionOk() {
        const issueId = issueAiNewSessionIssueId.trim();
        if (!issueId) {
            message.warning(ze('请输入 Issue ID', 'Please input Issue ID'));
            return;
        }
        setIssueAiNewSessionModalOpen(false);
        // 使用和【问题详情→AI分析】完全一致的流程：
        // startChatBotWithIssue 会切换工作区 → 查历史 → 无则 embed 模式自动开始新分析
        aiDrawerSlice.startChatBotWithIssue(issueId, selectedIssueAiAppId);
    }
    const renderWorkspacePicker = () => (_jsx(UnifiedWorkspacePicker
    // 海外不传 CrashClaw 列表，选择器只剩问题分析工作区
    , { 
        // 海外不传 CrashClaw 列表，选择器只剩问题分析工作区
        crashClawWorkspaces: EnvUtil.isChinaEnv() ? crashClawWorkspaceOptions : [], issueAiGameWorkspaces: issueAiGameWorkspaceOptions, selection: unifiedWorkspaceSelection, open: wsPopoverOpen, onOpenChange: setWsPopoverOpen, onSelect: handleSelectUnifiedWorkspace, onRefresh: () => {
            fetchCrashClawWorkspaces();
            fetchIssueAiGameWorkspaces();
            fetchCodebuddyHealth();
        }, isAiAgentWorkspace: isAiAgentWorkspace, showManageLink: EnvUtil.isChinaEnv() && crashClawWorkspaces.length > 0, showKnotWorkspace: canUseKnotAssistant !== false, codebuddyOnline: codebuddyOnline }));
    /**
     * 模型选择胶囊（已隐藏，保持与 v1 一致）。
     * 原为 CodeBuddy 工作区专用模型选择器，现统一使用后端默认模型。
     */
    const renderModelPicker = () => {
        return null;
        // 以下代码保留以便后续如需恢复模型选择功能
        /* eslint-disable no-unreachable */
        if (!isCodeBuddyWorkspace)
            return null;
        return (_jsx(Popover, { content: _jsx("div", { className: scss.clawModelPopover, children: _jsx("div", { className: scss.clawModelPopoverList, children: modelOptions.map((model) => {
                        const isSelected = model.value === selectedModel;
                        return (_jsxs("div", { className: `${scss.clawModelPopoverItem} ${isSelected ? scss.clawModelPopoverItemActive : ''}`, onClick: (event) => {
                                event.stopPropagation();
                                setSelectedModel(model.value);
                                setModelPopoverOpen(false);
                            }, children: [_jsx("span", { className: scss.clawModelPopoverItemName, children: model.label }), isSelected && _jsx(CheckOutlined, { className: scss.clawWsPopoverItemCheck })] }, model.value));
                    }) }) }), trigger: "click", open: modelPopoverOpen, onOpenChange: setModelPopoverOpen, placement: "topLeft", overlayStyle: { width: 260 }, destroyTooltipOnHide: false, children: _jsxs("div", { className: scss.clawModelPickerBtn, onClick: (event) => event.stopPropagation(), children: [_jsx(BulbOutlined, { className: scss.clawModelPickerIcon }), _jsx("span", { className: scss.clawModelPickerName, title: selectedModelLabel, children: selectedModelLabel }), _jsx(DownOutlined, { className: scss.clawModelPickerChevron })] }) }));
        /* eslint-enable no-unreachable */
    };
    /**
     * 底部输入区域，复用 AiChatBotInputBox 组件。
     */
    const inputArea = (_jsx("div", { style: { padding: '16px' }, children: _jsx(AiChatBotInputBox, { disableSend: isAnswerOutputting, isStreaming: isAnswerOutputting, onStop: stopCurrentConversation, onSend: (v) => handleSend(v), onAttach: isCrashClawWorkspaceSelected ? (files, text) => handleAttach(files, text) : undefined, leftElement: usesKnotAgentUI ? (_jsxs("div", { className: scss.clawInputPickerCluster, children: [renderWorkspacePicker(), renderModelPicker()] })) : null }) }));
    /** 渲染"你还可以直接问我"推荐问题列表 */
    function renderSuggestSection(intentionKey) {
        const questions = QUICK_SUGGEST_MAP[intentionKey];
        if (!questions || questions.length === 0)
            return null;
        return (_jsxs("div", { className: scss.suggestSection, children: [_jsx("div", { className: scss.suggestHeader, children: _jsx("span", { className: scss.suggestTitle, children: ze('你还可以直接问我：', 'You can also ask me:') }) }), _jsx("div", { className: scss.suggestList, children: questions.map((q) => (_jsxs("button", { className: scss.suggestItem, onClick: () => handleSend(q), children: [_jsx(BulbOutlined, { className: scss.suggestIcon }), _jsx("span", { className: scss.suggestText, children: q })] }, q))) })] }));
    }
    /**
     * 首页欢迎页面
     * 上半部分：左右布局的欢迎语 + AI Logo banner
     * 下半部分：四大场景卡片列表
     * 底部：输入框
     */
    const homePageDom = (_jsxs("div", { className: scss.homePageBody, children: [_jsxs("div", { className: scss.welcomeBanner, children: [_jsxs("div", { className: scss.welcomeText, children: [_jsx("div", { className: scss.welcomeGreeting, children: "Hi~" }), _jsx("div", { className: scss.welcomeTitle, children: ze('您的一站式崩溃分析助手已上线！', 'Your All-in-One Crash Analysis Assistant is Online!') })] }), _jsx("div", { className: scss.welcomeLogo, children: _jsx(WelcomeLogoIcon, { style: { transform: 'scale(2.5)', transformOrigin: 'center' } }) })] }), _jsxs("div", { className: scss.scenarioCardList, children: [SCENARIO_CARDS.map((card) => {
                        const { IconSvg } = card;
                        return (_jsxs("div", { className: scss.scenarioCard, onClick: () => onSelectScenario(card.intention), children: [_jsx("div", { className: scss.scenarioCardIcon, children: _jsx(IconSvg, { style: { width: 40, height: 40 } }) }), _jsxs("div", { className: scss.scenarioCardContent, children: [_jsx("div", { className: scss.scenarioCardTitle, children: card.title }), _jsx("div", { className: scss.scenarioCardSubtitle, children: card.subtitle })] }), _jsx(RightOutlined, { className: scss.scenarioCardArrow })] }, card.intention));
                    }), isCrashClawCardVisible && (_jsxs("div", { className: `${scss.scenarioCard} ${scss.scenarioCardCrashClaw}`, onClick: onSelectCrashClawCard, children: [_jsx("div", { className: scss.scenarioCardIcon, children: _jsx(ClawSourceIcon, { style: { width: 40, height: 40 } }) }), _jsxs("div", { className: scss.scenarioCardContent, children: [_jsx("div", { className: scss.scenarioCardTitle, children: ze('CrashClaw 源码分析', 'CrashClaw Source Analysis') }), _jsx("div", { className: scss.scenarioCardSubtitle, children: ze('基于源码的崩溃根因分析 & 审查', 'Source-based crash root cause analysis & review') })] }), _jsx(Tooltip, { title: ze('CrashClaw 配置', 'CrashClaw Settings'), children: _jsxs("span", { className: scss.scenarioCardSettingBtn, onClick: onClickCrashClawCardSetting, children: [_jsx(SettingOutlined, {}), _jsx("span", { className: scss.scenarioCardSettingText, children: ze('设置', 'Settings') })] }) })] }))] }), renderSuggestSection('HOME'), _jsx("div", { style: { flexGrow: 1 } }), inputArea] }));
    /**
     * 场景子页面
     * 居中展示场景图标 + 主标题 + 副标题 + 推荐问题，底部输入框
     */
    const scenarioSubPageDom = activeScenario ? (() => {
        const { IconSvg } = activeScenario;
        return (_jsxs("div", { className: scss.scenarioSubPageBody, children: [_jsxs("div", { className: scss.scenarioSubPageContent, children: [_jsx("div", { className: scss.scenarioSubPageIcon, children: _jsx(IconSvg, { style: { width: 64, height: 64 } }) }), _jsx("div", { className: scss.scenarioSubPageTitle, children: activeScenario.pageTitle }), _jsx("div", { className: scss.scenarioSubPageSubtitle, children: activeScenario.pageSubtitle })] }), renderSuggestSection(activeScenario.intention), _jsx("div", { style: { flexGrow: 1 } }), inputArea] }));
    })() : null;
    /**
     * CrashClaw 内容区域
     * 完全委托给 CrashClawChatPanel 独立组件处理：
     *   - 未配置 Workspace → 引导卡片
     *   - 已配置 → Workspace 选择器 + 场景卡片/聊天界面
     */
    const crashClawContentDom = (_jsx(CrashClawChatPanel, { onConversationListChange: setClawConversationList, onCurrentChatIdChange: handleClawCurrentChatIdChange, selectChatId: clawSelectChatId, selectChatSeq: clawSelectChatSeq, newSessionSignal: clawNewSessionSignal, refreshHistorySignal: clawRefreshHistorySignal, onActiveIntentionChange: setClawActiveIntention, backToHomeSignal: clawBackToHomeSignal, onSelectedWorkspaceUidChange: setClawSelectedWorkspaceUid, panelActive: activeProduct === 'CRASHCLAW', controlledSelectedWorkspaceUid: isCrashClawWorkspaceSelected ? unifiedWorkspaceSelection : undefined, controlledWorkspaces: crashClawWorkspaces, controlledWorkspacesLoaded: crashClawWorkspacesLoaded, hideInternalPicker: true, externalLeftElement: isCrashClawWorkspaceSelected ? renderWorkspacePicker() : null, restoreChatId: clawRestoreChatId, onRestoreChatIdConsumed: () => setClawRestoreChatId(null), issueAnalysisWorkspaceUid: aiDrawerSlice.pendingCrashClawWorkspaceUid || undefined, issueAnalysisSessionId: aiDrawerSlice.pendingCrashClawSessionId || undefined, issueAnalysisAutoSendText: aiDrawerSlice.pendingCrashClawAutoSendText || undefined, issueAnalysisAppId: aiDrawerSlice.pendingCrashClawIssueAppId || undefined, issueAnalysisIssueId: aiDrawerSlice.pendingCrashClawIssueId || undefined, issueAnalysisSeq: aiDrawerSlice.pendingCrashClawSeq, onIssueAnalysisConsumed: consumePendingCrashClawIntent }));
    /**
     * 主内容区域布局：
     *   header → Divider → 内容区域
     *
     * 内容区域根据 activeProduct 和会话状态选择性渲染：
     * - AI_AGENT + 空会话 + 无场景 → homePageDom（欢迎页 + 场景卡片）
     * - AI_AGENT + 空会话 + 有场景 → scenarioSubPageDom（场景子页面）
     * - AI_AGENT + 有会话       → 对话气泡列表 + 输入框
     * - CRASHCLAW              → crashClawHomeDom（始终显示首页，不共享 AI Agent 的会话）
     */
    // 关键：把两个 Tab 内容同时挂载，用 display:none 隐藏未激活的 Tab。
    // 这样切到 AI Agent 时 CrashClaw 的 stream/state 不会因卸载而中断（P0-4）。
    // - aiAgentBlock 仅在 AI_AGENT 激活时显示，CrashClaw block 同理。
    // - 两个 block 都使用 flex 列布局填充剩余空间，子组件结构维持原状。
    // 海外抽屉只展示问题分析内容区，避免 selection 收敛前闪出 CrashSight AI / CrashClaw 壳
    const isOverseasChatDrawer = !EnvUtil.isChinaEnv();
    const aiAgentBlock = (_jsxs("div", { style: {
            display: !isOverseasChatDrawer && usesKnotAgentUI ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
        }, children: [isOnHomePage && homePageDom, isInScenarioSubPage && scenarioSubPageDom, !isEmptyConversation && (_jsxs(_Fragment, { children: [_jsx(AiDrawerAutoScrollBody, { forceScrollToBottomTrigger: undefined, children: bubbleDomList }), isTooManyRounds && (_jsx(Alert, { message: _jsxs("div", { children: [_jsx("span", { children: ze('当前会话的对话轮次过多，即将达到系统预设容量上限，建议开启新的会话。', 'Current conversation has too many rounds of dialogue, please start a new conversation.') }), _jsx(Button, { type: "link", size: "small", onClick: handleStartNewConversation, style: {
                                        padding: '0 4px',
                                        marginLeft: '8px',
                                        height: 'auto',
                                        lineHeight: 'inherit',
                                        verticalAlign: 'baseline',
                                    }, children: ze('开始新会话', 'Start New Conversation') })] }), type: "warning" })), inputArea] }))] }));
    const crashClawBlock = (_jsx("div", { style: {
            display: !isOverseasChatDrawer && activeProduct === 'CRASHCLAW' ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
        }, children: crashClawContentDom }));
    const issueAiBlock = (_jsx("div", { style: {
            // 海外：即使尚未收敛到 ISSUE_AI，也先展示问题分析空态，避免空白抽屉
            display: activeProduct === 'ISSUE_AI' || isOverseasChatDrawer ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
        }, children: selectedIssueAiSession.issueId && selectedIssueAiSession.conversationId ? (_jsx(AiIssueAnalysisDrawerBody, { issueId: selectedIssueAiSession.issueId, trigger: `${selectedIssueAiSession.issueId}-history-${selectedIssueAiSession.conversationId}`, embedInChatDrawer: true, embedConversationId: isPlaceholderIssueAiConversationId(selectedIssueAiSession.conversationId)
                ? ''
                : selectedIssueAiSession.conversationId, forceNewAnalysis: selectedIssueAiSession.conversationId === ISSUE_AI_CONVERSATION_NEW, overrideAppId: selectedIssueAiAppId, overridePid: appIdToPid[selectedIssueAiAppId], bottomBarLeftElement: activeProduct === 'ISSUE_AI' || isOverseasChatDrawer ? renderWorkspacePicker() : null }, `${selectedIssueAiSession.issueId}::${selectedIssueAiSession.conversationId}`)) : (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }, children: [_jsxs("div", { style: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', padding: 40, textAlign: 'center' }, children: [_jsx("div", { style: { fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#555' }, children: ze('暂无当前游戏的分析记录', 'No analysis records for this game') }), _jsxs("div", { style: { fontSize: 13, lineHeight: 1.8, maxWidth: 360 }, children: [_jsx("p", { children: ze('分析会话来源于【问题列表】→【问题详情】→ 点击右上角【AI分析】按钮。', 'Analysis sessions come from [Issue List] → [Issue Detail] → click [AI Analysis] button.') }), _jsx("p", { style: { marginTop: 8 }, children: ze('无法在此直接创建分析会话。', 'Cannot create analysis sessions directly here.') }), _jsx("p", { style: { marginTop: 8 }, children: ze('可点击右上角历史会话按钮，通过 Issue ID 搜索已有的分析记录。', 'Click the history button above to search existing analysis records by Issue ID.') })] })] }), _jsx("div", { style: { padding: '12px 16px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }, children: activeProduct === 'ISSUE_AI' || isOverseasChatDrawer ? renderWorkspacePicker() : null })] })) }));
    // CODEBUDDY 工作区已并入上方 aiAgentBlock（复用 CrashSight AI 的完整聊天 UI），
    // 不再使用独立的 CodeBuddyChatPanel。
    const mainContentDom = (_jsxs("div", { className: scss.aiDrawer, style: { flex: 1, minWidth: 0 }, children: [headerDom, _jsx(Divider, { style: { margin: '0' } }), aiAgentBlock, crashClawBlock, issueAiBlock] }));
    // --- 全屏 vs 非全屏布局 ---
    // 海外全屏侧栏也只走问题分析会话列表，避免仍停在 ai_agent 时露出 Knot 历史
    const isAiAgent = !isOverseasChatDrawer && usesKnotAgentUI;
    const isCrashClaw = !isOverseasChatDrawer && activeProduct === 'CRASHCLAW';
    // 全屏时 CrashClaw 模式在右侧展示产物预览面板
    const showFullscreenPreview = chatBotFullscreen && isCrashClaw;
    return (_jsxs("div", { className: scss.aiThemeScope, style: { display: 'flex', height: chatBotFullscreen ? '100%' : '100vh', width: '100%' }, children: [chatBotFullscreen ? (_jsx(AiChatBotConversationList, { conversationList: isAiAgent ? conversationList : isCrashClaw ? clawConversationList : issueAiHistoryList, currentConversationId: isAiAgent ? conversationId : isCrashClaw ? clawCurrentChatId : selectedIssueAiSessionId, onSelectConversation: isAiAgent ? onSelectConversation : isCrashClaw ? onSelectClawConversation : onSelectIssueAiSession, onNewConversation: isAiAgent ? onClickNewConversation : isCrashClaw ? onClawNewConversation : onIssueAiNewSession, onRenameConversation: isCrashClaw ? renameClawConversation : undefined })) : null, mainContentDom, false && showFullscreenPreview && (_jsxs("div", { className: scss.fullscreenPreviewPanel, children: [_jsx("div", { className: scss.fullscreenPreviewTitle, children: ze('产物预览', 'Report Preview') }), _jsx("div", { className: scss.fullscreenPreviewBody, children: ze('对话中的产物报告将在此处展示', 'Report artifacts from the conversation will appear here') })] })), _jsx(Modal, { title: ze('重命名会话', 'Rename conversation'), open: !!historyRenameTarget, confirmLoading: historyRenaming, onOk: handleHistoryRenameOk, onCancel: () => {
                    setHistoryRenameTarget(null);
                    setHistoryRenameValue('');
                }, destroyOnClose: true, children: _jsx(Input, { value: historyRenameValue, maxLength: 120, onChange: (e) => setHistoryRenameValue(e.target.value), onPressEnter: handleHistoryRenameOk, placeholder: ze('输入会话名称', 'Enter session name') }) }), _jsxs(Modal, { title: ze('新建问题分析', 'New Issue Analysis'), open: issueAiNewSessionModalOpen, onOk: handleIssueAiNewSessionOk, onCancel: () => {
                    setIssueAiNewSessionModalOpen(false);
                    setIssueAiNewSessionIssueId('');
                }, destroyOnClose: true, children: [_jsx("div", { style: { marginBottom: 8, fontSize: 13, color: '#8c8c8c' }, children: ze('输入要分析的 Issue ID，将开始一次全新的 AI 分析（类似从问题详情页点击"AI分析"）。', 'Enter the Issue ID to analyze. This starts a new AI analysis (similar to clicking "AI Analysis" from the issue detail page).') }), _jsxs("div", { style: { marginBottom: 8, fontSize: 13 }, children: [ze('当前游戏 AppId', 'Current AppId'), ": ", _jsx("span", { style: { fontWeight: 600 }, children: selectedIssueAiAppId })] }), _jsx(Input, { value: issueAiNewSessionIssueId, onChange: (e) => setIssueAiNewSessionIssueId(e.target.value), onPressEnter: handleIssueAiNewSessionOk, placeholder: ze('请输入 Issue ID', 'Please input Issue ID') })] })] }));
};
export default AiChatBotEx;
